/**
 * @fileoverview Creately Legacy Format Converter
 * 
 * Converts exported data from the old Creately app format to the new app's Scene format.
 * 
 * Supports two formats:
 * 1. Clipboard format: { shapesToCopy: [...] } - array of shapes
 * 2. Diagram format: { model: { shapes: {...} } } - object with shape IDs as keys
 * 
 * The old Creately format contains:
 * - shapesToCopy: Array of shape objects with complex nested structure (clipboard)
 * - model.shapes: Object with shape IDs as keys (actual diagram)
 * - Each shape has: id, defId, x, y, texts, style, etc.
 * - Connectors are separate entries with path data
 * - Connector bars represent marriages/unions
 * 
 * The new app format uses:
 * - Scene with nodes[] and edges[]
 * - Nodes: id, type, name, sex, x, y, width, height, etc.
 * - Edges: id, type, from, to, attrs
 */

import type { Scene, Node, Edge } from '../core/models/scene';
import {
  buildLegacyCustomImageUrl,
  isAbsoluteMediaUrl,
  type LegacyCustomImageUrlOptions,
} from './legacyCustomImageUrl';
import {
  readLegacyDataImageRawRef,
  readLegacyShapeImagesRawRef,
} from './legacyOrgPhotoRef';

export type CreatelyLegacyConverterOptions = LegacyCustomImageUrlOptions;

interface LegacyShapeImageRef {
  file?: string;
  fileType?: string;
}

type LegacyShapeImages = Record<string, LegacyShapeImageRef | unknown> & {
  _imageURLSource?: LegacyShapeImageRef;
};

/**
 * Old Creately shape data structure (partial type for conversion)
 */
/**
 * Shape structure for clipboard format (nested in data property)
 */
interface CreatelyClipboardShape {
  data: {
    id: string;
    defId: string;
    x: number;
    y: number;
    type: 'edata' | 'basic' | 'connector' | 'freehand';
    texts?: {
      [key: string]: {
        content?: Array<{
          text?: string;
          color?: string;
          size?: number;
          font?: string;
        }>;
        value?: string;
      };
    };
    style?: {
      fillColor?: string;
      lineColor?: string;
      textColor?: string;
    };
    data?: {
      [key: string]: any;
    };
    angle?: number;
    connectorBarData?: {
      shapeAId: string;
      shapeBId: string;
    };
    path?: {
      headId: string;
      tailId: string;
      [key: string]: {
        shapeId?: string;
        gluepointId?: string;
        x?: number;
        y?: number;
      } | string | undefined;
    };
    connectorBarShapeAId?: string;
    connectorBarShapeBId?: string;
    connectorIds?: string[];
    zIndex?: number;
    defaultBounds?: {
      top: number;
      left: number;
      width: number;
      height: number;
    };
    entryClass?: string;
    instructions?: string[];
    scaleX?: number;
    scaleY?: number;
    images?: LegacyShapeImages;
  };
  images?: LegacyShapeImages;
  bounds?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  type: 'edata' | 'basic' | 'connector';
}

/**
 * Shape structure for diagram format (flat, not nested)
 */
interface CreatelyDiagramShape {
  id: string;
  defId: string;
  x: number;
  y: number;
  type: 'edata' | 'basic' | 'connector';
  texts?: {
    [key: string]: {
      content?: Array<{
        text?: string;
        color?: string;
        size?: number;
        font?: string;
        align?: string;
        bold?: boolean;
        italic?: boolean;
        underline?: boolean;
        strikeout?: boolean;
      }>;
      value?: string;
    };
  };
  style?: {
    fillColor?: string;
    lineColor?: string;
    textColor?: string;
    lineThickness?: number;
  };
  data?: {
    [key: string]: any;
  };
  connectorBarData?: {
    shapeAId: string;
    shapeBId: string;
  };
  path?: {
    headId: string;
    tailId: string;
    [key: string]: {
      shapeId?: string;
      gluepointId?: string;
      x?: number;
      y?: number;
    } | string | undefined;
  };
  connectorBarShapeAId?: string;
  connectorBarShapeBId?: string;
  connectorIds?: string[];
  zIndex?: number;
  defaultBounds?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  scaleX?: number;
  scaleY?: number;
  userSetWidth?: number;
  userSetHeight?: number;
  angle?: number;
  entryClass?: string;
  instructions?: string[];
  features?: Array<{
    id: string;
    data?: {
      value?: any;
    };
  }>;
  styleDefinitions?: {
    [cellId: string]: {
      style?: {
        lineThickness?: number;
        lineColor?: string;
        textColor?: string;
        fillColor?: string;
      };
    };
  };
  tableStyles?: {
    margins?: number[];
  };
  images?: LegacyShapeImages;
}

interface CreatelyClipboardExport {
  shapesToCopy?: CreatelyClipboardShape[];
  shapeIds?: string[];
}

interface CreatelyDiagramExport {
  model?: {
    shapes?: {
      [shapeId: string]: CreatelyDiagramShape;
    };
    name?: string;
    lastUpdated?: {
      $numberLong?: string;
    } | number;
  };
  static?: {
    createdTime?: {
      $numberLong?: string;
    } | number;
  };
}

/**
 * Unified normalized shape interface for processing
 */
interface NormalizedShape {
  id: string;
  defId: string;
  type: 'edata' | 'basic' | 'connector';
  x: number;
  y: number;
  texts?: {
    [key: string]: {
      content?: Array<{ text?: string; [key: string]: any }>;
      value?: string;
    };
  };
  style?: {
    fillColor?: string;
    lineColor?: string;
    textColor?: string;
    lineThickness?: number;
  };
  data?: {
    [key: string]: any;
  };
  angle?: number;
  connectorBarData?: {
    shapeAId: string;
    shapeBId: string;
  };
  connectorBarShapeAId?: string;
  connectorBarShapeBId?: string;
  path?: {
    headId: string;
    tailId: string;
    [key: string]: {
      shapeId?: string;
      gluepointId?: string;
      x?: number;
      y?: number;
    } | string | undefined;
  };
  zIndex?: number;
  defaultBounds?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  bounds?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  scaleX?: number;
  scaleY?: number;
  userSetWidth?: number;
  userSetHeight?: number;
  entryClass?: string;
  instructions?: string[];
  features?: Array<{
    id: string;
    data?: {
      value?: any;
    };
  }>;
  styleDefinitions?: {
    [cellId: string]: {
      style?: {
        lineThickness?: number;
        lineColor?: string;
        textColor?: string;
        fillColor?: string;
      };
    };
  };
  tableStyles?: {
    margins?: number[];
  };
  images?: LegacyShapeImages;
}

/**
 * Normalize MongoDB-style numbers (e.g., { $numberLong: "123" } -> 123)
 */
function normalizeNumber(value: any): number | undefined {
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object') {
    if ('$numberLong' in value) {
      return parseInt(String(value.$numberLong), 10);
    }
  }
  return undefined;
}

/**
 * Normalize clipboard shape to unified format
 */
function normalizeClipboardShape(shape: CreatelyClipboardShape): NormalizedShape {
  // Handle freehand type (old app uses "freehand" as type, but we normalize it to "basic" for processing)
  const normalizedType = (shape.data.type === 'freehand' ? 'basic' : shape.data.type) as 'edata' | 'basic' | 'connector';
  return {
    id: shape.data.id,
    defId: shape.data.defId,
    type: normalizedType,
    x: shape.data.x ?? shape.bounds?.left ?? 0,
    y: shape.data.y ?? shape.bounds?.top ?? 0,
    texts: shape.data.texts,
    style: shape.data.style,
    data: shape.data.data,
    angle: shape.data.angle,
    connectorBarData: shape.data.connectorBarData,
    connectorBarShapeAId: shape.data.connectorBarShapeAId,
    connectorBarShapeBId: shape.data.connectorBarShapeBId,
    path: shape.data.path,
    zIndex: shape.data.zIndex,
    defaultBounds: shape.data.defaultBounds,
    bounds: shape.bounds,
    entryClass: shape.data.entryClass,
    instructions: shape.data.instructions,
    scaleX: shape.data.scaleX,
    scaleY: shape.data.scaleY,
    images: shape.images ?? (shape.data?.images as LegacyShapeImages | undefined),
  };
}

/**
 * Normalize diagram shape to unified format
 */
function normalizeDiagramShape(shape: CreatelyDiagramShape): NormalizedShape {
  return {
    id: shape.id,
    defId: shape.defId,
    type: shape.type,
    x: shape.x,
    y: shape.y,
    texts: shape.texts,
    style: shape.style,
    data: shape.data,
    images: shape.images ?? (shape.data?.images as LegacyShapeImages | undefined),
    angle: shape.angle,
    connectorBarData: shape.connectorBarData,
    connectorBarShapeAId: shape.connectorBarShapeAId,
    connectorBarShapeBId: shape.connectorBarShapeBId,
    path: shape.path,
    zIndex: shape.zIndex,
    defaultBounds: shape.defaultBounds,
    scaleX: shape.scaleX,
    scaleY: shape.scaleY,
    userSetWidth: shape.userSetWidth,
    userSetHeight: shape.userSetHeight,
    entryClass: shape.entryClass,
    instructions: shape.instructions,
    features: shape.features,
    styleDefinitions: shape.styleDefinitions,
    tableStyles: shape.tableStyles,
  };
}

/**
 * Extract text from a text data object (handles content array and value)
 */
function extractTextFromTextData(textData: { content?: Array<{ text?: string }>; value?: string }): string | null {
  // Priority 1: content array (contains actual text segments)
  if (textData.content && Array.isArray(textData.content)) {
    const extracted = textData.content
      .map(c => c.text || '')
      .join('')
      .trim();
    if (extracted) return extracted;
  }
  
  // Priority 2: value field (but skip if it looks like a placeholder/label)
  if (textData.value) {
    // Some legacy shapes (notably table cells) store `value` as a JSON-quoted HTML string,
    // e.g. "\"<span style=...></span>\"". Decode it before stripping tags.
    let rawValue = textData.value;
    const trimmedValue = rawValue.trim();
    if (
      (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
      (trimmedValue.startsWith("'") && trimmedValue.endsWith("'"))
    ) {
      try {
        const parsed = JSON.parse(trimmedValue);
        if (typeof parsed === 'string') rawValue = parsed;
      } catch {
        // keep original rawValue
      }
    }

    const cleaned = rawValue.replace(/<[^>]*>/g, '').trim();
    // Skip common placeholder values
    if (cleaned && 
        cleaned !== 'Title' && 
        cleaned !== 'Text' && 
        cleaned !== 'Description' &&
        cleaned !== '<p></p>' &&
        cleaned !== '""' &&
        cleaned !== "''" &&
        cleaned.length > 0) {
      return cleaned;
    }
  }
  
  return null;
}

/**
 * Extract text content from normalized shape (generic, data-driven approach)
 */
function extractText(shape: NormalizedShape): string {
  // Strategy 1: Extract from texts object (most reliable)
  if (shape.texts) {
    // Try all text fields, prioritizing primary fields
    const primaryFields = ['title', 'main', 'text', 'description', 'note', 'content'];
    const allFields = Object.keys(shape.texts);
    
    // First pass: try primary fields
    for (const field of primaryFields) {
      if (shape.texts[field]) {
        const extracted = extractTextFromTextData(shape.texts[field]);
        if (extracted) return extracted;
      }
    }
    
    // Second pass: try all other fields
    for (const field of allFields) {
      if (!primaryFields.includes(field) && shape.texts[field]) {
        const extracted = extractTextFromTextData(shape.texts[field]);
        if (extracted) return extracted;
      }
    }
  }
  
  // Strategy 2: Extract from data object (fallback for shapes that store text differently)
  if (shape.data) {
    // Try common data fields that might contain text
    const dataTextFields = ['title', 'text', 'description', 'name', 'label', 'content'];
    for (const field of dataTextFields) {
      const fieldData = shape.data[field];
      if (fieldData) {
        // Handle { value: "..." } structure
        if (fieldData.value && typeof fieldData.value === 'string') {
          const cleaned = fieldData.value.replace(/<[^>]*>/g, '').trim();
          if (cleaned && cleaned !== '<p></p>') return cleaned;
        }
        // Handle direct string value
        if (typeof fieldData === 'string') {
          const cleaned = fieldData.replace(/<[^>]*>/g, '').trim();
          if (cleaned && cleaned !== '<p></p>') return cleaned;
        }
      }
    }
  }
  
  return '';
}

function normalizeLegacyConnectorLabel(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\r\n/g, '\n').trim();
}

function extractLegacyConnectorLabel(shape: NormalizedShape): { label?: string; labelOffset?: number } {
  const texts = shape.texts;
  if (!texts || typeof texts !== 'object') return {};

  const blocks = Object.values(texts) as Array<any>;
  if (!Array.isArray(blocks) || blocks.length === 0) return {};

  const getBlockText = (block: any): string => {
    if (!block || typeof block !== 'object') return '';
    if (!Array.isArray(block.content)) return '';
    return normalizeLegacyConnectorLabel(
      block.content.map((run: any) => (typeof run?.text === 'string' ? run.text : '')).join('')
    );
  };

  // Prefer primary only when it contains real text; many legacy connectors have
  // primary text blocks that are just "\n" placeholders.
  const primaryBlock = blocks.find((b) => b && typeof b === 'object' && b.primary === true);
  const primaryText = getBlockText(primaryBlock);
  const nonEmptyBlock =
    (primaryText ? primaryBlock : null) ||
    blocks.find((b) => {
      const t = getBlockText(b);
      return t.length > 0;
    }) ||
    null;

  if (!nonEmptyBlock || typeof nonEmptyBlock !== 'object') return {};

  const label = getBlockText(nonEmptyBlock);
  const pos = nonEmptyBlock.pos;
  const labelOffset = typeof pos === 'number' ? clamp01(pos) : undefined;

  return {
    ...(label ? { label } : {}),
    ...(labelOffset !== undefined ? { labelOffset } : {}),
  };
}

function isLegacyOrgPersonNodeType(nodeType: Node['type']): boolean {
  return nodeType === 'org_person_photo' ||
    nodeType === 'org_person_photo_left' ||
    nodeType === 'org_person_photo_right' ||
    nodeType === 'org_person_photo_inner_left' ||
    nodeType === 'org_person_text';
}

function isLegacyOrgPersonPhotoNodeType(nodeType: Node['type']): boolean {
  return nodeType === 'org_person_photo' ||
    nodeType === 'org_person_photo_left' ||
    nodeType === 'org_person_photo_right' ||
    nodeType === 'org_person_photo_inner_left';
}

function extractLegacyOrgPhotoRef(
  shape: NormalizedShape,
  options?: CreatelyLegacyConverterOptions,
): { rawRef: string; photoRef: string } | null {
  const dataRawRef = readLegacyDataImageRawRef(shape.data as Record<string, unknown> | undefined);
  const nestedImages =
    shape.images ??
    (shape.data?.images as LegacyShapeImages | undefined);
  const { rawRef: imagesRawRef, fileType } = readLegacyShapeImagesRawRef(nestedImages);

  const rawRef = dataRawRef || imagesRawRef;
  if (!rawRef) return null;

  if (fileType === 'url' || isAbsoluteMediaUrl(rawRef)) {
    return { rawRef, photoRef: rawRef };
  }

  const photoRef = buildLegacyCustomImageUrl(rawRef, options);
  if (!photoRef) return null;
  return { rawRef, photoRef };
}

function normalizeLegacyOrgTextLine(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\u00a0/g, ' ').trim();
}

function extractLegacyOrgLinesFromTextField(
  textData: { content?: Array<{ text?: string }>; value?: string } | undefined
): string[] {
  const lines: string[] = [];

  if (textData?.content && Array.isArray(textData.content)) {
    for (const segment of textData.content) {
      const segmentText = typeof segment?.text === 'string' ? segment.text : '';
      if (!segmentText) continue;
      const splitLines = segmentText
        .split(/\r?\n/)
        .map((line) => normalizeLegacyOrgTextLine(line))
        .filter((line) => line.length > 0);
      lines.push(...splitLines);
    }
  }

  // Only parse HTML value when rich content is unavailable.
  // Some legacy shapes keep placeholder labels (e.g., Name/Designation)
  // in value while actual text lives in content.
  if (lines.length === 0 && typeof textData?.value === 'string') {
    const htmlLines = textData.value
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, '');
    const splitLines = htmlLines
      .split(/\r?\n/)
      .map((line) => normalizeLegacyOrgTextLine(line))
      .filter((line) => line.length > 0);
    lines.push(...splitLines);
  }

  return lines;
}

/**
 * Extract legacy org person name/title from rich text runs.
 *
 * Old org cards commonly encode Name in the first run/line and Job Title
 * (designation) in the second run/line under texts.main.
 */
function extractLegacyOrgPersonNameAndRole(shape: NormalizedShape): { name?: string; role?: string } {
  const mainLines = extractLegacyOrgLinesFromTextField(shape.texts?.main);
  const secondaryLines = extractLegacyOrgLinesFromTextField(shape.texts?.secondary);

  let name = mainLines[0];
  let role = secondaryLines[0] || mainLines[1];

  if (!name || !role) {
    const fallbackText = extractText(shape);
    if (fallbackText) {
      const fallbackLines = fallbackText
        .split(/\r?\n/)
        .map((line) => normalizeLegacyOrgTextLine(line))
        .filter((line) => line.length > 0);
      if (!name) {
        name = fallbackLines[0];
      }
      if (!role) {
        role = fallbackLines[1];
      }
    }
  }

  return {
    ...(name ? { name } : {}),
    ...(role ? { role } : {}),
  };
}

/**
 * Extract fontSize from normalized shape's texts object
 * Looks for size field in text content arrays
 * Prioritizes primary text fields (title, main, text, etc.)
 * 
 * Note: Old app can have different font sizes for different substrings in content array.
 * New app applies formatting to full text, so we use the first non-empty text item's size.
 */
function extractFontSize(shape: NormalizedShape): number | undefined {
  if (!shape.texts) return undefined;
  
  // Priority fields for text nodes and sticky notes
  const primaryFields = ['title', 'main', 'text', 'description', 'note', 'content', 'identifier'];
  const allFields = Object.keys(shape.texts);
  
  // Helper to extract fontSize from content array
  // Uses first non-empty text item's size (since new app applies formatting to full text)
  const getFontSizeFromContent = (content: Array<{ size?: number; text?: string }>): number | undefined => {
    if (!Array.isArray(content) || content.length === 0) return undefined;
    
    // First, try to find size from first content item with actual text (not just whitespace/newlines)
    for (const item of content) {
      if (item && typeof item === 'object' && item.size && typeof item.size === 'number' && item.size > 0) {
        const text = item.text;
        // If text exists and is not just whitespace/newlines, use this size
        if (text !== undefined && text !== null && typeof text === 'string' && text.trim().length > 0) {
          return item.size;
        }
      }
    }
    
    // Fallback: use first valid size found (even if text is empty or missing)
    for (const item of content) {
      if (item && typeof item === 'object' && item.size && typeof item.size === 'number' && item.size > 0) {
        return item.size;
      }
    }
    
    return undefined;
  };
  
  // First pass: try primary fields (prioritize these for sticky notes and text nodes)
  for (const field of primaryFields) {
    const textData = shape.texts[field];
    if (textData?.content && Array.isArray(textData.content)) {
      const fontSize = getFontSizeFromContent(textData.content);
      if (fontSize) return fontSize;
    }
    
    // Also check if there's a fontSize field directly on textData
    if (textData && typeof textData === 'object' && 'fontSize' in textData) {
      const fontSize = (textData as any).fontSize;
      if (typeof fontSize === 'number' && fontSize > 0) {
        return fontSize;
      }
    }
  }
  
  // Second pass: try all other fields
  for (const field of allFields) {
    if (!primaryFields.includes(field)) {
      const textData = shape.texts[field];
      if (textData?.content && Array.isArray(textData.content)) {
        const fontSize = getFontSizeFromContent(textData.content);
        if (fontSize) return fontSize;
      }
      
      if (textData && typeof textData === 'object' && 'fontSize' in textData) {
        const fontSize = (textData as any).fontSize;
        if (typeof fontSize === 'number' && fontSize > 0) {
          return fontSize;
        }
      }
    }
  }
  
  // Fallback: check style.textSize or style.fontSize
  if (shape.style) {
    const style = shape.style as any;
    if (style.textSize && typeof style.textSize === 'number' && style.textSize > 0) {
      return style.textSize;
    }
    if (style.fontSize && typeof style.fontSize === 'number' && style.fontSize > 0) {
      return style.fontSize;
    }
  }
  
  // Fallback: check data.defaultTextFormat.size
  if (shape.data?.defaultTextFormat?.size) {
    const size = shape.data.defaultTextFormat.size;
    if (typeof size === 'number' && size > 0) {
      return size;
    }
  }
  
  return undefined;
}

/**
 * Map old app's entryClass to new app's routingType
 * 
 * Old app entryClass values:
 * - "connectors.bundle.js#ConnectorSmoothAngled" → "orthogonalRounded" (right-angled rounded)
 * - "connectors.bundle.js#ConnectorAngled" → "orthogonal" (right-angled)
 * - "connectors.bundle.js#ConnectorIndented" → "orthogonal" (right-angled)
 * - "connectors.bundle.js#ConnectorStraight" → "straight"
 * - "connectors.bundle.js#ConnectorCurved" → "bezier" (curved)
 * 
 * Default: "orthogonal" (smooth angled) - matches old app's default
 */
function mapEntryClassToRoutingType(entryClass?: string): 'straight' | 'orthogonal' | 'orthogonalRounded' | 'bezier' {
  if (!entryClass || typeof entryClass !== 'string') {
    return 'orthogonal'; // Default to smooth angled (old app's default)
  }
  
  // Extract the connector type from entryClass (e.g., "ConnectorSmoothAngled" from "connectors.bundle.js#ConnectorSmoothAngled")
  const connectorType = entryClass.split('#').pop()?.toLowerCase() || '';
  
  // Map to new app routing types
  if (connectorType.includes('straight')) {
    return 'straight';
  } else if (connectorType.includes('curved')) {
    return 'bezier'; // Curved maps to bezier in new app
  } else if (connectorType.includes('smoothangled')) {
    return 'orthogonalRounded'; // Smooth angled should preserve rounded corners
  } else if (connectorType.includes('angled') || 
             connectorType.includes('indented')) {
    return 'orthogonal'; // Sharp angled variants map to orthogonal
  }
  
  // Default: orthogonal (smooth angled - old app's default)
  return 'orthogonal';
}

/**
 * Parse freehand instructions and convert to points array
 * 
 * Instructions format:
 * - moveTo(sw * X + xo, sh * Y + yo) - first point
 * - lineTo(sw * X + xo, sh * Y + yo) - subsequent points
 * 
 * Where:
 * - sw = scaleX (width scale)
 * - sh = scaleY (height scale)  
 * - xo = x offset (shape x position)
 * - yo = y offset (shape y position)
 * 
 * Returns flat array of numbers: [x1, y1, x2, y2, ...]
 */
function parseFreehandInstructions(
  instructions: string[] | undefined,
  x: number,
  y: number,
  scaleX: number = 1,
  scaleY: number = 1
): number[] {
  if (!instructions || !Array.isArray(instructions) || instructions.length === 0) {
    return [];
  }
  
  const points: number[] = [];
  const sw = scaleX;
  const sh = scaleY;
  const xo = x;
  const yo = y;
  
  for (const instruction of instructions) {
    if (typeof instruction !== 'string') continue;
    
    // Parse moveTo or lineTo commands
    // Pattern: moveTo(sw * X + xo,sh * Y + yo) or lineTo(sw * X + xo,sh * Y + yo)
    // Handle optional whitespace around operators and comma
    const moveToMatch = instruction.match(/moveTo\s*\(\s*sw\s*\*\s*([\d.]+)\s*\+\s*xo\s*,\s*sh\s*\*\s*([\d.]+)\s*\+\s*yo\s*\)/);
    const lineToMatch = instruction.match(/lineTo\s*\(\s*sw\s*\*\s*([\d.]+)\s*\+\s*xo\s*,\s*sh\s*\*\s*([\d.]+)\s*\+\s*yo\s*\)/);
    
    if (moveToMatch || lineToMatch) {
      const match = moveToMatch || lineToMatch;
      if (match && match[1] && match[2]) {
        try {
          // Evaluate the expressions (they're just numbers, e.g., "0", "22.430715935334888")
          const relX = parseFloat(match[1].trim());
          const relY = parseFloat(match[2].trim());
          
          if (!isNaN(relX) && !isNaN(relY)) {
            // Convert to absolute coordinates
            const absX = sw * relX + xo;
            const absY = sh * relY + yo;
            points.push(absX, absY);
          }
        } catch (_e) {
          // Skip invalid coordinates
          continue;
        }
      }
    }
  }
  
  return points;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0.5;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function inferAnchorFromPoint(
  node: Node,
  point: { x?: number; y?: number } | undefined
): { anchor: 'top' | 'right' | 'bottom' | 'left'; offset: number } | null {
  const px = point?.x;
  const py = point?.y;
  if (!Number.isFinite(px) || !Number.isFinite(py)) {
    return null;
  }

  const left = Number.isFinite(node.x) ? node.x : 0;
  const top = Number.isFinite(node.y) ? node.y : 0;
  const width = Math.max(1, Number(node.width ?? node.w ?? 80));
  const height = Math.max(1, Number(node.height ?? node.h ?? 80));
  const right = left + width;
  const bottom = top + height;

  const distances: Array<{ anchor: 'top' | 'right' | 'bottom' | 'left'; distance: number }> = [
    { anchor: 'top', distance: Math.abs((py as number) - top) },
    { anchor: 'right', distance: Math.abs((px as number) - right) },
    { anchor: 'bottom', distance: Math.abs((py as number) - bottom) },
    { anchor: 'left', distance: Math.abs((px as number) - left) },
  ];

  distances.sort((a, b) => a.distance - b.distance);
  const bestAnchor = distances[0]?.anchor;
  if (!bestAnchor) return null;

  if (bestAnchor === 'top' || bestAnchor === 'bottom') {
    return {
      anchor: bestAnchor,
      offset: clamp01(((px as number) - left) / width),
    };
  }

  return {
    anchor: bestAnchor,
    offset: clamp01(((py as number) - top) / height),
  };
}

/**
 * Determine flowchart edge type and attrs from old Creately flowchart connector defId.
 *
 * Supported legacy connector defIds:
 * - creately.flowchart.flow.* -> sequence_flow
 * - creately.flowchart.decisionyes.* -> sequence_flow with label "Yes"
 * - creately.flowchart.decisionno.* -> sequence_flow with label "No"
 */
function getFlowchartEdgeTypeAndAttrsFromDefId(defId: string): {
  type: Edge['type'];
  attrs?: Record<string, unknown>;
} | null {
  const lowerDefId = defId.toLowerCase();
  const flowchartMatch = lowerDefId.match(/creately\.flowchart\.([a-z0-9-]+)(?:\.\d+)?$/);
  const flowchartToken = flowchartMatch?.[1];
  if (!flowchartToken) return null;

  if (flowchartToken === 'flow') {
    return {
      type: 'sequence_flow' as Edge['type'],
      attrs: {
        dashed: false,
        startArrow: { type: 'none', size: 6 },
        endArrow: { type: 'triangle', size: 8 },
      },
    };
  }

  if (flowchartToken === 'decisionyes' || flowchartToken === 'decisionno') {
    return {
      type: 'sequence_flow' as Edge['type'],
      attrs: {
        label: flowchartToken === 'decisionyes' ? 'Yes' : 'No',
        dashed: false,
        startArrow: { type: 'none', size: 6 },
        endArrow: { type: 'triangle', size: 8 },
      },
    };
  }

  return null;
}

/**
 * Map legacy orgchart shape defIds to the closest supported org node types.
 *
 * We preserve visual intent (especially image/text alignment) using the existing
 * org variants until dedicated legacy variants are introduced.
 */
function getOrgchartNodeTypeAndAttrsFromDefId(defId: string): {
  nodeType: Node['type'];
  attrs?: Record<string, unknown>;
} | null {
  const lowerDefId = defId.toLowerCase();
  const tokenMatch = lowerDefId.match(/creately\.(?:orgchart|org\.chart)\.([a-z0-9_-]+)/);
  const token = tokenMatch?.[1]?.replace(/_/g, '-');
  if (!token) return null;

  const personTextLeft = {
    nodeType: 'org_person_text' as Node['type'],
    attrs: { variant: 'org_person_text', shapeType: 'org_person_text', textAlignment: 'left' },
  };
  const personTextCenter = {
    nodeType: 'org_person_text' as Node['type'],
    attrs: { variant: 'org_person_text', shapeType: 'org_person_text', textAlignment: 'center' },
  };

  const map: Record<string, { nodeType: Node['type']; attrs?: Record<string, unknown> }> = {
    'top-centered-image': { nodeType: 'org_person_photo', attrs: { variant: 'org_person_photo', shapeType: 'org_person_photo' } },
    'top-left-image': { nodeType: 'org_person_photo_left', attrs: { variant: 'org_person_photo_left', shapeType: 'org_person_photo_left' } },
    'center-align-image': { nodeType: 'org_person_photo', attrs: { variant: 'org_person_photo', shapeType: 'org_person_photo', textAlignment: 'center' } },
    'horizontal-align-image': { nodeType: 'org_person_photo_inner_left', attrs: { variant: 'org_person_photo_inner_left', shapeType: 'org_person_photo_inner_left' } },
    'left-align-image': { nodeType: 'org_person_photo_left', attrs: { variant: 'org_person_photo_left', shapeType: 'org_person_photo_left', textAlignment: 'left' } },
    'no-image': personTextLeft,
    textonly: personTextCenter,
    circularimagetop: { nodeType: 'org_person_photo', attrs: { variant: 'org_person_photo', shapeType: 'org_person_photo' } },
    circularimagenobase: { nodeType: 'org_person_photo', attrs: { variant: 'org_person_photo', shapeType: 'org_person_photo' } },
    squareimagetop: { nodeType: 'org_person_photo', attrs: { variant: 'org_person_photo', shapeType: 'org_person_photo' } },
    squareimageleft: { nodeType: 'org_person_photo_left', attrs: { variant: 'org_person_photo_left', shapeType: 'org_person_photo_left', textAlignment: 'left' } },
    department: { nodeType: 'org_department', attrs: { variant: 'org_department', shapeType: 'org_department' } },
  };

  return map[token] || null;
}

/**
 * Map legacy orgchart connector defIds to org relationship edge semantics.
 */
function getOrgchartEdgeTypeAndAttrsFromDefId(defId: string): {
  type: Edge['type'];
  attrs?: Record<string, unknown>;
} | null {
  const lowerDefId = defId.toLowerCase();
  const tokenMatch = lowerDefId.match(/creately\.(?:orgchart|org\.chart)\.([a-z0-9_-]+)/);
  const token = tokenMatch?.[1]?.replace(/_/g, '-');
  if (!token) return null;

  // Normalize token separators so reportsto/reports_to/reports-to are treated equivalently.
  const canonicalToken = token.replace(/[-_]/g, '');

  if (canonicalToken === 'reportsto') {
    return {
      type: 'reporting',
      attrs: {
        relation: 'reports_to',
        dashed: false,
        routingType: 'rounded',
        startArrow: { type: 'none', size: 6 },
        endArrow: { type: 'triangle', size: 8 },
      },
    };
  }

  return null;
}

/**
 * Determine edge type and attributes from Creately connector defId
 * Maps genogram connector types to edge types and attributes based on new app's relationship pack
 * 
 * Partner/Legal & Romantic relationships:
 * - divorce → marriage with divorced: true
 * - separation → marriage with separated: true
 * - cohabitation → marriage with house: true, dashed: true
 * - engagement → marriage with engaged: true, dashed: true
 * 
 * Sibling relationships:
 * - sibling → sibling
 * - half-sibling/half_sibling → sibling with half: true (or use sibling type)
 * - twin-mono/twin_mono → sibling with twin_mono: true
 * - twin-di/twin_di → sibling with twin_di: true
 * 
 * Parent-Child relationships:
 * - child → child
 * 
 * Emotional relationships:
 * - friendship → sibling (or line, depending on context)
 */
function getEdgeTypeAndAttrsFromDefId(defId: string): {
  type: Edge['type'];
  attrs?: Record<string, unknown>;
} {
  if (!defId) {
    return { type: 'line' };
  }
  
  const lowerDefId = defId.toLowerCase();

  // Flowchart-specific connector mapping
  const flowchartEdge = getFlowchartEdgeTypeAndAttrsFromDefId(defId);
  if (flowchartEdge) {
    return flowchartEdge;
  }

  // Orgchart-specific connector mapping
  const orgchartEdge = getOrgchartEdgeTypeAndAttrsFromDefId(defId);
  if (orgchartEdge) {
    return orgchartEdge;
  }
  
  // Extract the connector type from defId (e.g., "creately.genogram.divorced" -> "divorced")
  const connectorTypeMatch = lowerDefId.match(/creately\.genogram\.(.+)$/);
  const connectorType = connectorTypeMatch ? connectorTypeMatch[1] : '';
  
  // Partner/Legal & Romantic relationships - all map to 'marriage' type with different attributes
  // Check for combined states first (e.g., engagement-and-separation)
  if (connectorType === 'engagement-and-separation' || lowerDefId.includes('engagement-and-separation')) {
    // Engagement that ended in separation - prioritize separation
    return { type: 'marriage', attrs: { separated: true, color: '#000000' } };
  }
  
  if (connectorType === 'engagement-and-cohabitation' || lowerDefId.includes('engagement-and-cohabitation')) {
    // Engagement that became cohabitation - prioritize cohabitation
    return { type: 'marriage', attrs: { house: true, dashed: true, color: '#2563eb' } };
  }
  
  if (connectorType === 'cohabitation-and-separation' || connectorType === 'legal-cohabitation-and-separation-infact' ||
      connectorType === 'legal-cohabitation-and-official-separation' || lowerDefId.includes('cohabitation-and-separation')) {
    // Cohabitation that ended in separation - prioritize separation
    return { type: 'marriage', attrs: { separated: true, color: '#000000' } };
  }
  
  if (connectorType === 'non-sentimental-cohabitation-and-separation' || lowerDefId.includes('non-sentimental-cohabitation-and-separation')) {
    return { type: 'marriage', attrs: { separated: true, color: '#000000' } };
  }
  
  // Check for legal variants
  if (connectorType === 'legal-separation' || lowerDefId.includes('legal-separation')) {
    return { type: 'marriage', attrs: { separated: true, color: '#000000' } };
  }
  
  if (connectorType === 'legal-cohabitation' || lowerDefId.includes('legal-cohabitation')) {
    return { type: 'marriage', attrs: { house: true, dashed: true, color: '#2563eb' } };
  }
  
  // Check for nullity
  if (connectorType === 'genogram-nullity' || connectorType === 'nullity' || lowerDefId.includes('nullity')) {
    return { type: 'marriage', attrs: { nullity: true, color: '#000000' } };
  }
  
  // Basic relationship types
  if (connectorType === 'divorced' || lowerDefId.includes('divorced')) {
    return { type: 'marriage', attrs: { divorced: true, color: '#000000' } };
  }
  
  if (connectorType === 'separation' || lowerDefId.includes('separation')) {
    return { type: 'marriage', attrs: { separated: true, color: '#000000' } };
  }
  
  if (connectorType === 'cohabitation' || lowerDefId.includes('cohabitation')) {
    return { type: 'marriage', attrs: { house: true, dashed: true, color: '#2563eb' } };
  }
  
  if (connectorType === 'engagement' || lowerDefId.includes('engagement')) {
    return { type: 'marriage', attrs: { engaged: true, dashed: true, color: '#2563eb' } };
  }
  
  // Marriage (default)
  if (connectorType === 'marriage' || lowerDefId.includes('marriage')) {
    return { type: 'marriage', attrs: { color: '#111827', dashed: false } };
  }
  
  // Parent-Child relationships
  if (connectorType === 'child' || lowerDefId.includes('child')) {
    return { type: 'child', attrs: {} };
  }
  
  // Sibling relationships
  if (connectorType === 'sibling' || (lowerDefId.includes('sibling') && !lowerDefId.includes('half'))) {
    return { type: 'sibling', attrs: {} };
  }
  
  if (connectorType === 'half-sibling' || connectorType === 'half_sibling' || lowerDefId.includes('half-sibling') || lowerDefId.includes('half_sibling')) {
    return { type: 'sibling', attrs: { half: true, dashed: true } };
  }
  
  if (connectorType === 'twin-mono' || connectorType === 'twin_mono' || lowerDefId.includes('twin-mono') || lowerDefId.includes('twin_mono')) {
    return { type: 'sibling', attrs: { twin_mono: true } };
  }
  
  if (connectorType === 'twin-di' || connectorType === 'twin_di' || lowerDefId.includes('twin-di') || lowerDefId.includes('twin_di')) {
    return { type: 'sibling', attrs: { twin_di: true } };
  }
  
  // Emotional relationships - map to 'line' type with attributes matching relationship pack
  // These map to the 'emotional' category in the new app's relationship pack
  
  // Friendship / Close relationships
  if (connectorType === 'friendship' || connectorType === 'veryclose' || connectorType === 'very-close' || 
      connectorType === 'best-friends' || connectorType === 'bestfriends' || lowerDefId.includes('friendship') || 
      lowerDefId.includes('veryclose') || lowerDefId.includes('best-friends')) {
    // Maps to 'close' relationship subtype: Friendship / Close
    return { type: 'line', attrs: { color: '#01A001', width: 3, dashed: true, dash: [6, 6], doubleLine: true, routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Love / In Love relationships
  if (connectorType === 'love' || connectorType === 'in-love' || connectorType === 'love-affair' || 
      lowerDefId.includes('love') && !lowerDefId.includes('affair')) {
    // Maps to 'love' relationship subtype: Love / In Love
    return { type: 'line', attrs: { color: '#00A000', width: 3, dashed: false, midCircle: true, routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Conflict / Discord relationships
  if (connectorType === 'conflict' || connectorType === 'conflictual' || connectorType === 'discord' || 
      lowerDefId.includes('conflict') || lowerDefId.includes('discord')) {
    // Maps to 'conflict' relationship subtype: Discord / Conflict
    return { type: 'line', attrs: { color: '#DC0101', width: 3, dashed: true, dash: [6, 6], doubleLine: true, routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Cutoff / Estranged relationships
  if (connectorType === 'cutoff' || connectorType === 'estranged' || connectorType === 'genogram-cutoff-repaired' ||
      lowerDefId.includes('cutoff') || lowerDefId.includes('estranged')) {
    // Maps to 'cutoff' relationship subtype: Cutoff / Estranged
    return { type: 'line', attrs: { color: '#DC0101', width: 3, dashed: true, dash: [6, 6], midTicks: true, routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Fused / Enmeshed relationships
  if (connectorType === 'fused' || connectorType === 'fusedhostile' || connectorType === 'fused-violence' ||
      lowerDefId.includes('fused') && !lowerDefId.includes('hostile') && !lowerDefId.includes('violence')) {
    // Maps to 'fused' relationship subtype: Fused (Enmeshed)
    return { type: 'line', attrs: { color: '#DC0101', width: 3, dashed: false, tripleLine: true, routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Violence / Abuse relationships
  if (connectorType === 'violence' || connectorType === 'abuse' || connectorType === 'physicalabuse' || 
      connectorType === 'emotionalabuse' || connectorType === 'sexualabuse' || connectorType === 'close-violence' ||
      connectorType === 'distant-violence' || connectorType === 'fused-violence' ||
      lowerDefId.includes('violence') || lowerDefId.includes('abuse')) {
    // Maps to 'violence' relationship subtype: Violence / Abuse
    return { type: 'line', attrs: { color: '#DC0101', width: 3, wave: true, routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Distant / Poor relationships
  if (connectorType === 'distant' || connectorType === 'distantrelationship' || connectorType === 'distanthostile' ||
      lowerDefId.includes('distant') && !lowerDefId.includes('violence')) {
    // Maps to 'distant' relationship subtype: Distant / Poor
    return { type: 'line', attrs: { color: '#000000', width: 3, dashed: true, dash: [6, 6], routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Indifferent / Apathetic relationships
  if (connectorType === 'indifferent' || connectorType === 'indifferentrelationship' ||
      lowerDefId.includes('indifferent')) {
    // Maps to 'indifferent' relationship subtype: Indifferent / Apathetic
    return { type: 'line', attrs: { color: '#000000', width: 3, dashed: true, dash: [4, 10], routingType: 'bezier', endArrow: { type: 'none' } } };
  }
  
  // Default: if it's a genogram connector but we don't recognize the type, use line
  if (lowerDefId.includes('creately.genogram.')) {
    return { type: 'line', attrs: {} };
  }
  
  // Non-genogram connectors default to line
  return { type: 'line', attrs: {} };
}

/**
 * Determine sex and additional attributes from Creately defId
 * Handles genogram shapes with sexual orientation and gender identity
 * Maps to new app's shape types (gay, lesbian, bisexual, transgender, etc.)
 */
function getSexAndAttrsFromDefId(defId: string): {
  sex: 'male' | 'female' | 'unknown';
  attrs?: Record<string, unknown>;
} {
  if (!defId) {
    return { sex: 'unknown' };
  }
  
  const lowerDefId = defId.toLowerCase();
  
  // Handle gender-based shapes (creately.genogram-gender-based.{shape-name})
  // Extract shape name and map to corresponding shapeType
  if (lowerDefId.includes('genogram-gender-based')) {
    // Extract the shape name after "genogram-gender-based."
    const genderBasedMatch = lowerDefId.match(/genogram-gender-based\.(.+?)(?:\.|$)/);
    if (genderBasedMatch) {
      let shapeName = genderBasedMatch[1];
      
      // Remove version suffix if present (e.g., ".1" at the end)
      shapeName = shapeName.replace(/\.\d+$/, '');
      
      // Map gender-based shape names to shapeType values
      // Check for female first (before male) to avoid substring matching issues
      if (shapeName === 'female' || shapeName === 'female-alternate') {
        return { sex: 'female', attrs: { shapeType: 'classic-female' } };
      }
      if (shapeName === 'male' || shapeName === 'male-alternate') {
        return { sex: 'male', attrs: { shapeType: 'classic-male' } };
      }
      
      // Map other gender-based shapes
      const genderBasedShapeMap: Record<string, { shapeType: string; sex: 'male' | 'female' | 'unknown' }> = {
        'agender': { shapeType: 'agender', sex: 'unknown' },
        'agender-alternate': { shapeType: 'agender-classic', sex: 'unknown' },
        'ambiamorous': { shapeType: 'ambiamorous', sex: 'unknown' },
        'androgyne': { shapeType: 'androgyne', sex: 'unknown' },
        'bigender': { shapeType: 'bigender', sex: 'unknown' },
        'centrgender': { shapeType: 'centrgender', sex: 'unknown' },
        'demiboy': { shapeType: 'demiboy', sex: 'male' },
        'demigender': { shapeType: 'demigender', sex: 'unknown' },
        'demigirl': { shapeType: 'demigirl', sex: 'female' },
        'demiril': { shapeType: 'demigender', sex: 'unknown' }, // Map demiril to demigender
        'detragender': { shapeType: 'detragender', sex: 'unknown' },
        'epicene': { shapeType: 'epicene', sex: 'unknown' },
        'epicene-alternate': { shapeType: 'epicene-classic', sex: 'unknown' },
        'gender-fluid': { shapeType: 'gender-fluid', sex: 'unknown' },
        'gender-fluid-alternate': { shapeType: 'gender-fluid-classic', sex: 'unknown' },
        'genderqueer': { shapeType: 'genderqueer', sex: 'unknown' },
        'hijra': { shapeType: 'hijra', sex: 'unknown' },
        'intergender': { shapeType: 'intergender', sex: 'unknown' },
        'intersex': { shapeType: 'intersex', sex: 'unknown' },
        'isogender': { shapeType: 'isogender', sex: 'unknown' },
        'monoflexible': { shapeType: 'monoflexible', sex: 'unknown' },
        'neurogender': { shapeType: 'neurogender', sex: 'unknown' },
        'nonamorous': { shapeType: 'nonamorous', sex: 'unknown' },
        'pangender': { shapeType: 'pangender', sex: 'unknown' },
        'polyamorous': { shapeType: 'polyamorous', sex: 'unknown' },
        'polyflexible': { shapeType: 'polyflexible', sex: 'unknown' },
        'queer': { shapeType: 'queer', sex: 'unknown' },
        'questioning': { shapeType: 'questioning', sex: 'unknown' },
        'self-partnering': { shapeType: 'self-partnering', sex: 'unknown' },
        'third-gender': { shapeType: 'third-gender', sex: 'unknown' },
        'transgender': { shapeType: 'transgender-classic', sex: 'unknown' },
        'transgender-alternate': { shapeType: 'transgender-alt', sex: 'unknown' },
        'travesti': { shapeType: 'travesti', sex: 'unknown' },
        'trigender': { shapeType: 'trigender', sex: 'unknown' },
        'trigender-alternate': { shapeType: 'trigender-equal', sex: 'unknown' },
        'two-spirit': { shapeType: 'two-spirit', sex: 'unknown' },
        'ultergender': { shapeType: 'ultergender', sex: 'unknown' },
        'ultergender-alternate': { shapeType: 'ultergender-equal', sex: 'unknown' },
        'xenogender': { shapeType: 'xenogender', sex: 'unknown' },
      };
      
      const mapping = genderBasedShapeMap[shapeName];
      if (mapping) {
        return { sex: mapping.sex, attrs: { shapeType: mapping.shapeType } };
      }
    }
  }
  
  // Extract the shape type from defId (e.g., "creately.genogram.lesbian" -> "lesbian")
  const shapeTypeMatch = lowerDefId.match(/creately\.genogram[.-](.+)$/);
  const shapeType = shapeTypeMatch ? shapeTypeMatch[1] : '';
  
  
  // Handle sexual orientation shapes - only set sexualOrientation in attrs, not shapeType
  // The app will derive shapeType from sexualOrientation automatically
  if (shapeType === 'lesbian' || lowerDefId.includes('lesbian')) {
    return { sex: 'female', attrs: { sexualOrientation: 'lesbian' } };
  }
  if (shapeType === 'gay' || (lowerDefId.includes('gay') && !lowerDefId.includes('bisexual'))) {
    return { sex: 'male', attrs: { sexualOrientation: 'gay' } };
  }
  // Handle bisexual shapes (with or without hyphens)
  if (shapeType === 'bisexualmale' || shapeType === 'bisexual-male' || 
      lowerDefId.includes('bisexualmale') || lowerDefId.includes('bisexual-male')) {
    return { sex: 'male', attrs: { sexualOrientation: 'bisexual' } };
  }
  if (shapeType === 'bisexualfemale' || shapeType === 'bisexual-female' || 
      lowerDefId.includes('bisexualfemale') || lowerDefId.includes('bisexual-female')) {
    return { sex: 'female', attrs: { sexualOrientation: 'bisexual' } };
  }
  
  // Handle transgender shapes (with or without hyphens)
  if (shapeType === 'transgenderfemale' || shapeType === 'transgender-female' || 
      lowerDefId.includes('transgenderfemale') || lowerDefId.includes('transgender-female')) {
    return { sex: 'female', attrs: { genderIdentity: 'transgender' } };
  }
  if (shapeType === 'transgendermale' || shapeType === 'transgender-male' || 
      lowerDefId.includes('transgendermale') || lowerDefId.includes('transgender-male')) {
    return { sex: 'male', attrs: { genderIdentity: 'transgender' } };
  }
  
  // Handle index person shapes
  if (shapeType.includes('index-person') || lowerDefId.includes('index-person')) {
    let indexShapeType = 'index-person';
    let sex: 'male' | 'female' | 'unknown' = 'male'; // Default
    
    if (shapeType.includes('female') || lowerDefId.includes('female')) {
      sex = 'female';
      indexShapeType = 'index-person-female';
    } else if (shapeType.includes('male') || lowerDefId.includes('male')) {
      sex = 'male';
      indexShapeType = 'index-person-male';
    } else {
      indexShapeType = 'index-person';
    }
    
    return { sex, attrs: { shapeType: indexShapeType, isIndexPerson: true } };
  }
  
  // Handle pregnancy
  if (shapeType === 'pregnant' || shapeType === 'pregnancy' || lowerDefId.includes('pregnant') || lowerDefId.includes('pregnancy')) {
    return { sex: 'female', attrs: { shapeType: 'pregnant', pregnancy: true } };
  }
  
  // Handle stillbirth
  if (shapeType.includes('stillbirth') || lowerDefId.includes('stillbirth')) {
    if (shapeType.includes('female') || lowerDefId.includes('female')) {
      return { sex: 'female', attrs: { shapeType: 'stillbirth-female', stillbirth: true } };
    }
    return { sex: 'male', attrs: { shapeType: 'stillbirth-male', stillbirth: true } };
  }

  // Handle immigration
  if (shapeType.includes('immigration') || lowerDefId.includes('immigration')) {
    if (shapeType.includes('female') || lowerDefId.includes('female')) {
      return { sex: 'female', attrs: { shapeType: 'immigration-female', immigration: true } };
    }
    return { sex: 'male', attrs: { shapeType: 'immigration-male', immigration: true } };
  }

  // Handle culture
  if (shapeType.includes('culture') || lowerDefId.includes('culture')) {
    if (shapeType.includes('female') || lowerDefId.includes('female')) {
      return { sex: 'female', attrs: { shapeType: 'culture-female', culture: true } };
    }
    return { sex: 'male', attrs: { shapeType: 'culture-male', culture: true } };
  }

  // Handle deceased
  if (shapeType.includes('deceased') || lowerDefId.includes('deceased')) {
    if (shapeType.includes('female') || lowerDefId.includes('female')) {
      return { sex: 'female', attrs: { shapeType: 'deceased-female' } };
    }
    if (shapeType.includes('male') || lowerDefId.includes('male')) {
      return { sex: 'male', attrs: { shapeType: 'deceased-male' } };
    }
    return { sex: 'unknown', attrs: { shapeType: 'deceased-unknown' } };
  }
  
  // Handle other special genogram shapes
  if (shapeType === 'institution' || lowerDefId.includes('institution')) {
    return { sex: 'unknown', attrs: { shapeType: 'institution', institution: true } };
  }
  if (shapeType === 'pet' || lowerDefId.includes('pet')) {
    return { sex: 'unknown', attrs: { shapeType: 'pet', pet: true } };
  }
  
  // Standard sex detection (check female first since "female" contains "male")
  if (lowerDefId.includes('female')) return { sex: 'female' };
  if (lowerDefId.includes('male')) return { sex: 'male' };
  
  return { sex: 'unknown' };
}

/**
 * Check if a defId is a genogram-related shape
 * Genogram shapes start with "creately.genogram." or "creately.genogram-"
 */
function isGenogramShape(defId: string): boolean {
  const lowerDefId = defId.toLowerCase();
  return lowerDefId.startsWith('creately.genogram.') || lowerDefId.startsWith('creately.genogram-');
}

function isOrgchartLikeNodeType(nodeType: Node['type']): boolean {
  const type = String(nodeType);
  return type === 'org-shape' || type.startsWith('org_');
}

type LegacySwimlaneOrientation = 'horizontal' | 'vertical';

interface LegacySwimlaneCell {
  id: string;
  rowId: number;
  columnId: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

function getLegacyFlowchartSwimlaneOrientation(defId: string): LegacySwimlaneOrientation | null {
  const lowerDefId = defId.toLowerCase();
  const tokenMatch = lowerDefId.match(/creately\.flowchart\.([a-z0-9-]+)(?:\.\d+)?$/);
  const token = tokenMatch?.[1];
  if (!token) return null;
  if (token === 'swimlaneh') return 'horizontal';
  if (token === 'swimlanev') return 'vertical';
  return null;
}

function extractLegacySwimlaneCells(shape: NormalizedShape): LegacySwimlaneCell[] {
  if (!shape.data || typeof shape.data !== 'object') return [];

  const cells: LegacySwimlaneCell[] = [];
  for (const entry of Object.values(shape.data)) {
    if (!entry || typeof entry !== 'object') continue;
    const entryRecord = entry as Record<string, any>;

    const value = entryRecord.value;
    const isExplicitCell = entryRecord.type === 'childShapeData';
    const looksLikeCellValue =
      value &&
      typeof value === 'object' &&
      typeof value.id === 'string' &&
      Number.isFinite(Number(value.rowId)) &&
      Number.isFinite(Number(value.columnId));

    if (!isExplicitCell && !looksLikeCellValue) continue;
    if (!looksLikeCellValue) continue;

    cells.push({
      id: String(value.id),
      rowId: Number(value.rowId),
      columnId: Number(value.columnId),
      x: Number(value.x ?? 0),
      y: Number(value.y ?? 0),
      width: Number(value.width ?? 0),
      height: Number(value.height ?? 0),
    });
  }

  return cells.filter((cell) =>
    cell.id.length > 0 &&
    Number.isFinite(cell.rowId) &&
    Number.isFinite(cell.columnId) &&
    Number.isFinite(cell.x) &&
    Number.isFinite(cell.y) &&
    Number.isFinite(cell.width) &&
    Number.isFinite(cell.height)
  );
}

function extractLegacySwimlaneCellText(shape: NormalizedShape, cellId: string): string {
  if (!shape.texts || !shape.texts[cellId]) return '';
  return extractTextFromTextData(shape.texts[cellId]) || '';
}

function averageCellSize(cells: LegacySwimlaneCell[], axis: 'row' | 'column', id: number): number {
  const matches = cells.filter((cell) => (axis === 'row' ? cell.rowId === id : cell.columnId === id));
  if (matches.length === 0) return 0;
  const total = matches.reduce((sum, cell) => sum + (axis === 'row' ? cell.height : cell.width), 0);
  return total / matches.length;
}

function buildLegacySwimlaneTableAttrs(
  shape: NormalizedShape,
  orientation: LegacySwimlaneOrientation,
  dimensions: { width: number; height: number }
): Record<string, unknown> {
  const cells = extractLegacySwimlaneCells(shape);
  if (cells.length === 0) {
    return {
      schemaId: orientation === 'horizontal' ? 'swimlane-horizontal' : 'swimlane-vertical',
      ...(orientation === 'horizontal'
        ? {
            rowCount: 3,
            columnCount: 2,
            showHeader: false,
            showHeaderColumn: true,
            headerRowIndices: [],
            headerColIndices: [0],
          }
        : {
            rowCount: 1,
            columnCount: 3,
            showHeader: true,
            showHeaderColumn: false,
            headerRowIndices: [0],
            headerColIndices: [],
            headerHeight: 40,
          }),
      rowHeights: orientation === 'horizontal' ? [200, 200, 200] : [Math.max(40, dimensions.height - 40)],
      columnWidths:
        orientation === 'horizontal' ? [Math.max(40, dimensions.width * 0.2), Math.max(60, dimensions.width * 0.8)] : [220, 220, 220],
    };
  }

  const rowIds = Array.from(new Set(cells.map((cell) => cell.rowId))).sort((a, b) => a - b);
  const columnIds = Array.from(new Set(cells.map((cell) => cell.columnId))).sort((a, b) => a - b);

  const rowHeights = rowIds.map((rowId) => Math.max(20, averageCellSize(cells, 'row', rowId)));
  const columnWidths = columnIds.map((columnId) => Math.max(20, averageCellSize(cells, 'column', columnId)));

  const textCountByRow = new Map<number, number>();
  const textCountByColumn = new Map<number, number>();
  const nonEmptyTextByCellId = new Map<string, string>();

  for (const cell of cells) {
    const text = extractLegacySwimlaneCellText(shape, cell.id).trim();
    if (!text) continue;
    nonEmptyTextByCellId.set(cell.id, text);
    textCountByRow.set(cell.rowId, (textCountByRow.get(cell.rowId) || 0) + 1);
    textCountByColumn.set(cell.columnId, (textCountByColumn.get(cell.columnId) || 0) + 1);
  }

  const pickHighestCount = (ids: number[], counter: Map<number, number>): number => {
    let best = ids[0] as number;
    let bestCount = -1;
    for (const id of ids) {
      const count = counter.get(id) || 0;
      if (count > bestCount) {
        best = id;
        bestCount = count;
      }
    }
    return best;
  };

  const primaryHeaderRowId = pickHighestCount(rowIds, textCountByRow);
  const primaryHeaderColumnId = pickHighestCount(columnIds, textCountByColumn);

  const headerRowIndex = Math.max(0, rowIds.indexOf(primaryHeaderRowId));
  const headerColumnIndex = Math.max(0, columnIds.indexOf(primaryHeaderColumnId));

  const cellText: Record<string, string> = {};
  const cellStyles: Record<string, { textRotation: 0 | 90 | -90 | 180 }> = {};

  if (orientation === 'horizontal') {
    for (const cell of cells) {
      if (cell.columnId !== primaryHeaderColumnId) continue;
      const text = nonEmptyTextByCellId.get(cell.id);
      if (!text) continue;
      const r = rowIds.indexOf(cell.rowId);
      if (r < 0) continue;
      const key = `${r},${headerColumnIndex}`;
      cellText[key] = text;
      cellStyles[key] = { textRotation: -90 };
    }
  } else {
    for (const cell of cells) {
      if (cell.rowId !== primaryHeaderRowId) continue;
      const text = nonEmptyTextByCellId.get(cell.id);
      if (!text) continue;
      const c = columnIds.indexOf(cell.columnId);
      if (c < 0) continue;
      const key = `${headerRowIndex},${c}`;
      cellText[key] = text;
    }
  }

  const styleEntries = Object.values(shape.styleDefinitions || {});
  const firstStyle = styleEntries.find((entry) => entry?.style)?.style;

  const headerCellIds =
    orientation === 'horizontal'
      ? cells.filter((cell) => cell.columnId === primaryHeaderColumnId).map((cell) => cell.id)
      : cells.filter((cell) => cell.rowId === primaryHeaderRowId).map((cell) => cell.id);

  let headerFillColor: string | undefined;
  for (const headerCellId of headerCellIds) {
    const style = shape.styleDefinitions?.[headerCellId]?.style;
    if (style?.fillColor && typeof style.fillColor === 'string') {
      headerFillColor = style.fillColor;
      break;
    }
  }

  const attrs: Record<string, unknown> = {
    schemaId: orientation === 'horizontal' ? 'swimlane-horizontal' : 'swimlane-vertical',
    rowCount: orientation === 'horizontal' ? rowIds.length : Math.max(1, rowIds.length - 1),
    columnCount: columnIds.length,
    rowHeights:
      orientation === 'horizontal'
        ? rowHeights
        : rowHeights.filter((_, index) => index !== headerRowIndex),
    columnWidths,
    showHeader: orientation === 'vertical',
    showHeaderColumn: orientation === 'horizontal',
    headerRowIndices: orientation === 'vertical' ? [headerRowIndex] : [],
    headerColIndices: orientation === 'horizontal' ? [headerColumnIndex] : [],
    ...(orientation === 'vertical' ? { headerHeight: rowHeights[headerRowIndex] || 40 } : {}),
    ...(Object.keys(cellText).length > 0 ? { cellText } : {}),
    ...(Object.keys(cellStyles).length > 0 ? { cellStyles } : {}),
  };

  if (headerFillColor) attrs.headerFillColor = headerFillColor;
  if (firstStyle?.lineColor && typeof firstStyle.lineColor === 'string') attrs.gridColor = firstStyle.lineColor;
  if (typeof firstStyle?.lineThickness === 'number' && Number.isFinite(firstStyle.lineThickness)) {
    attrs.strokeWidth = firstStyle.lineThickness;
  }

  return attrs;
}

/**
 * Detect legacy "creately.basic.table" defId (plain grid table, not swimlane).
 */
function isLegacyBasicTableDefId(defId: string | undefined): boolean {
  if (!defId) return false;
  const lower = defId.toLowerCase();
  return lower === 'creately.basic.table' || lower.endsWith('.basic.table');
}

/**
 * Build new-app table attrs for a legacy "creately.basic.table" shape.
 *
 * Legacy format stores cells under `data.<cellId>` with type `childShapeData` and
 * relative `x/y/width/height` plus 1-based `rowId/columnId`. Per-cell text lives
 * under `texts[<cellId>]` using the standard text data shape. We derive row/column
 * counts, sizes, and a 0-based "row,col" cellText map suitable for TableShapeNode.
 */
function buildLegacyBasicTableAttrs(
  shape: NormalizedShape,
  dimensions: { width: number; height: number }
): Record<string, unknown> {
  const cells = extractLegacySwimlaneCells(shape);

  if (cells.length === 0) {
    return {
      schemaId: 'blank',
      rowCount: 3,
      columnCount: 3,
      showHeader: false,
      showHeaderColumn: false,
      headerRowIndices: [],
      headerColIndices: [],
      rowHeights: Array(3).fill(Math.max(20, dimensions.height / 3)),
      columnWidths: Array(3).fill(Math.max(20, dimensions.width / 3)),
    };
  }

  const rowIds = Array.from(new Set(cells.map((cell) => cell.rowId))).sort((a, b) => a - b);
  const columnIds = Array.from(new Set(cells.map((cell) => cell.columnId))).sort((a, b) => a - b);

  const rowHeights = rowIds.map((rowId) => Math.max(20, averageCellSize(cells, 'row', rowId)));
  const columnWidths = columnIds.map((columnId) => Math.max(20, averageCellSize(cells, 'column', columnId)));

  const cellText: Record<string, string> = {};
  for (const cell of cells) {
    const text = extractLegacySwimlaneCellText(shape, cell.id).trim();
    if (!text) continue;
    const r = rowIds.indexOf(cell.rowId);
    const c = columnIds.indexOf(cell.columnId);
    if (r < 0 || c < 0) continue;
    cellText[`${r},${c}`] = text;
  }

  const styleEntries = Object.values(shape.styleDefinitions || {});
  const firstStyle = styleEntries.find((entry) => entry?.style)?.style;

  const attrs: Record<string, unknown> = {
    schemaId: 'blank',
    rowCount: rowIds.length,
    columnCount: columnIds.length,
    rowHeights,
    columnWidths,
    showHeader: false,
    showHeaderColumn: false,
    headerRowIndices: [],
    headerColIndices: [],
    ...(Object.keys(cellText).length > 0 ? { cellText } : {}),
  };

  if (firstStyle?.lineColor && typeof firstStyle.lineColor === 'string') {
    attrs.gridColor = firstStyle.lineColor;
  }
  if (typeof firstStyle?.lineThickness === 'number' && Number.isFinite(firstStyle.lineThickness)) {
    attrs.strokeWidth = firstStyle.lineThickness;
  }

  return attrs;
}


/**
 * Comprehensive mapping from Creately defId to NodeType
 * Maps old app shapes to new app node types, preserving visual information
 */
function getNodeType(defId: string): { type: Node['type']; mapped: boolean } {
  const lowerDefId = defId.toLowerCase();

  if (getLegacyFlowchartSwimlaneOrientation(defId)) {
    return { type: 'table', mapped: true };
  }

  if (isLegacyBasicTableDefId(defId)) {
    return { type: 'table', mapped: true };
  }
  
  // Person nodes (genogram shapes) - all genogram shapes are persons
  if (isGenogramShape(defId)) {
    return { type: 'person', mapped: true };
  }
  
  // Sticky notes
  if (lowerDefId.includes('sticky') || 
      lowerDefId.includes('datasource.lib.sticky') ||
      lowerDefId.includes('note')) {
    return { type: 'sticky-note', mapped: true };
  }
  
  // Text nodes
  if (lowerDefId.includes('text') && 
      !lowerDefId.includes('textbox')) {
    return { type: 'text', mapped: true };
  }
  
  // Frames/containers
  if (lowerDefId.includes('frame') || 
      lowerDefId.includes('container') ||
      lowerDefId.includes('group')) {
    return { type: 'frame', mapped: true };
  }
  
  // Freehand shapes
  if (lowerDefId === 'creately.basic.freehand' || lowerDefId.includes('freehand')) {
    return { type: 'freehand', mapped: true };
  }
  
  // Concept map shapes - map to 'basic-shape' type
  if (lowerDefId.includes('concept-map') || lowerDefId.includes('conceptmap')) {
    return { type: 'basic-shape', mapped: true };
  }
  
  // Basic shapes from old app - map to 'basic-shape' type
  // Match any defId that contains 'creately.basic.' or 'basic.' (more general pattern)
  // This preserves visual information even if not genogram-specific
  if (lowerDefId.includes('creately.basic.') || 
      (lowerDefId.includes('basic.') && !lowerDefId.includes('flowchart') && !lowerDefId.includes('org'))) {
    return { type: 'basic-shape', mapped: true };
  }
  
  // Flowchart shapes - map to 'flowchart-shape' type
  // Check for various flowchart shape patterns
  if (lowerDefId.includes('flowchart') ||
      lowerDefId.includes('process') ||
      lowerDefId.includes('startend') ||
      lowerDefId.includes('terminator') ||
      lowerDefId.includes('decision') ||
      lowerDefId.includes('data') ||
      lowerDefId.includes('document') ||
      lowerDefId.includes('predefined.process') ||
      lowerDefId.includes('predefined.startend')) {
    return { type: 'flowchart-shape', mapped: true };
  }
  
  // Org chart shapes - map to 'org-shape' type
  if (lowerDefId.includes('orgchart') || lowerDefId.includes('org.chart')) {
    return { type: 'org-shape', mapped: true };
  }
  
  // Other diagram shapes (UML, ERD, network) - map to basic-shape as fallback
  if (lowerDefId.includes('uml') ||
      lowerDefId.includes('erd') ||
      lowerDefId.includes('network')) {
    return { type: 'basic-shape', mapped: true };
  }
  
  // Default: map to basic-shape for all unmapped shapes
  // This ensures all shapes are visible in the new app (basic-shape is rendered, while 'shape' is not)
  // The variant will default to 'rectangle' if not specified, ensuring proper rendering
  return { type: 'basic-shape', mapped: false };
}

/**
 * Map legacy flowchart defIds from old Creately exports to new app flowchart manifest IDs.
 *
 * Examples:
 * - creately.flowchart.startend.3 -> start-end
 * - creately.flowchart.predefinedprocess.2 -> predefined
 * - creately.flowchart.documents.4 -> multi-doc
 */
function mapFlowchartVariantFromDefId(defId: string): string | undefined {
  const lowerDefId = defId.toLowerCase();

  // Parse token after "creately.flowchart." while ignoring trailing numeric version.
  const tokenMatch = lowerDefId.match(/creately\.flowchart\.([a-z0-9-]+)(?:\.\d+)?$/);
  const token = tokenMatch?.[1];
  if (!token) return undefined;

  const flowchartVariantMap: Record<string, string> = {
    startend: 'start-end',
    data: 'input-output',
    process: 'process',
    decision: 'decision',
    document: 'document',
    documents: 'multi-doc',
    display: 'display',
    directdata: 'database',
    storeddata: 'storage',
    manualinput: 'manual',
    predefinedprocess: 'predefined',
    internalstorage: 'internal',
    sequentialaccessstorage: 'storage',
    database: 'database',
    connector: 'on-page-connector',
    offpageref: 'off-page-connector',
    logicaland: 'junction',
    logicalor: 'or',
    delay: 'delay',
    loop: 'manual-operation',
    looplimit: 'loop-limit',
    collate: 'collate',
    merge: 'merge',
    preparation: 'preparation',
    sort: 'sort',
  };

  return flowchartVariantMap[token];
}

/**
 * Extract style information from normalized shape
 */
function extractStyle(shape: NormalizedShape): {
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
} {
  const style: {
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
  } = {};
  
  if (shape.style) {
    if (shape.style.fillColor) {
      style.fillColor = shape.style.fillColor;
    }
    if (shape.style.lineColor) {
      style.strokeColor = shape.style.lineColor;
    }
    if (shape.style.lineThickness) {
      style.strokeWidth = shape.style.lineThickness;
    }
  }
  
  // Default stroke width
  if (!style.strokeWidth) {
    style.strokeWidth = 2;
  }
  
  return style;
}

/**
 * Get dimensions from normalized shape
 */
function getDimensions(shape: NormalizedShape): { width: number; height: number } {
  // Try user-set dimensions first (diagram format)
  if (shape.userSetWidth && shape.userSetHeight) {
    return {
      width: shape.userSetWidth,
      height: shape.userSetHeight,
    };
  }
  
  // Try scale-based dimensions (diagram format)
  if (shape.scaleX && shape.scaleY && shape.defaultBounds) {
    return {
      width: shape.defaultBounds.width * shape.scaleX,
      height: shape.defaultBounds.height * shape.scaleY,
    };
  }
  
  // Try bounds (clipboard format)
  if (shape.bounds) {
    return {
      width: shape.bounds.width || 80,
      height: shape.bounds.height || 80,
    };
  }
  
  // Try default bounds
  if (shape.defaultBounds) {
    return {
      width: shape.defaultBounds.width || 80,
      height: shape.defaultBounds.height || 80,
    };
  }
  
  // Default for genogram persons
  return { width: 80, height: 80 };
}

/**
 * Conversion statistics for logging
 */
interface ConversionStats {
  totalShapes: number;
  nodesCreated: number;
  edgesCreated: number;
  connectorBarsFound: number;
  connectorsFound: number;
  skippedShapes: number;
  unmappedDefIds: Set<string>;
  nodeTypes: Map<string, number>;
  edgeTypes: Map<string, number>;
}

/**
 * Unified function to process normalized shapes into Scene format
 */
function processShapesToScene(
  shapes: NormalizedShape[],
  metadata?: { createdAt?: number; updatedAt?: number },
  options?: CreatelyLegacyConverterOptions,
): Scene {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const connectorBarMap = new Map<string, { shapeAId: string; shapeBId: string }>();
  
  // Initialize statistics
  const stats: ConversionStats = {
    totalShapes: shapes.length,
    nodesCreated: 0,
    edgesCreated: 0,
    connectorBarsFound: 0,
    connectorsFound: 0,
    skippedShapes: 0,
    unmappedDefIds: new Set(),
    nodeTypes: new Map(),
    edgeTypes: new Map(),
  };
  
  // Filter shapes: process all shapes except connectors (connectors are edges)
  // Note: We process all shapes (not filtered) to handle connector bars and connectors correctly
  
  // First pass: collect all shapes and connector bars
  // Use all shapes (not filtered) to find connector bars
  for (const shape of shapes) {
    // Handle connector bars (marriage bars)
    if (shape.defId === 'creately.basic.connectorbar') {
      stats.connectorBarsFound++;
      if (shape.connectorBarData) {
        connectorBarMap.set(shape.id, {
          shapeAId: shape.connectorBarData.shapeAId,
          shapeBId: shape.connectorBarData.shapeBId,
        });
      } else if (shape.connectorBarShapeAId && shape.connectorBarShapeBId) {
        connectorBarMap.set(shape.id, {
          shapeAId: shape.connectorBarShapeAId,
          shapeBId: shape.connectorBarShapeBId,
        });
      }
      stats.skippedShapes++;
      continue; // Skip connector bars as nodes
    }
    
    // Handle connectors (edges) - skip for now, process in second pass
    // In old app: type === 'connector' means it's an edge in new app
    if (shape.type === 'connector') {
      stats.connectorsFound++;
      stats.skippedShapes++;
      continue;
    }

    // Skip shapes with missing ids (they would otherwise produce Yjs items whose
    // parentSub === undefined and break Y.encodeStateAsUpdate downstream).
    if (typeof shape.id !== 'string' || shape.id.length === 0) {
      stats.skippedShapes++;
      console.warn('[Creately Converter] Skipping shape with missing id:', shape.defId);
      continue;
    }

    // Extract position (mutable for freehand shapes which need position adjustment)
    let x = shape.x ?? 0;
    let y = shape.y ?? 0;
    const dimensions = getDimensions(shape);
    const text = extractText(shape);
    const fontSize = extractFontSize(shape);
    const { type: inferredNodeType, mapped: inferredMapped } = getNodeType(shape.defId || '');
    const orgNodeMapping = getOrgchartNodeTypeAndAttrsFromDefId(shape.defId || '');
    const nodeType = orgNodeMapping?.nodeType ?? inferredNodeType;
    const mapped = inferredMapped || Boolean(orgNodeMapping);
    const legacyOrgPersonText = isLegacyOrgPersonNodeType(nodeType)
      ? extractLegacyOrgPersonNameAndRole(shape)
      : null;
    
    // Extract text style properties (fontSize, fontFamily, fontWeight, fontStyle, textColor, etc.)
    // These are needed for person node labels
    const textStyleAttrs: Record<string, unknown> = {};
    if (fontSize) {
      textStyleAttrs.fontSize = fontSize;
    }
    
    // Extract font family, weight, style, color from text content
    if (shape.texts) {
      const primaryFields = ['title', 'main', 'text', 'description', 'note', 'content'];
      for (const field of primaryFields) {
        const textData = shape.texts[field];
        if (textData?.content && Array.isArray(textData.content) && textData.content.length > 0) {
          // Get first non-empty text item for style extraction
          const firstTextItem = textData.content.find((item: any) => 
            item && typeof item === 'object' && item.text && typeof item.text === 'string' && item.text.trim().length > 0
          ) || textData.content[0];
          
          if (firstTextItem && typeof firstTextItem === 'object') {
            // Extract font family
            if (firstTextItem.font && typeof firstTextItem.font === 'string') {
              textStyleAttrs.fontFamily = firstTextItem.font;
            }
            
            // Extract font weight (bold)
            if (firstTextItem.bold === true || firstTextItem.bold === 'true') {
              textStyleAttrs.fontWeight = 'bold';
            }
            
            // Extract font style (italic)
            if (firstTextItem.italic === true || firstTextItem.italic === 'true') {
              textStyleAttrs.fontStyle = 'italic';
            }
            
            // Extract text decoration (underline, strikeout)
            if (firstTextItem.underline === true || firstTextItem.underline === 'true') {
              textStyleAttrs.textDecoration = 'underline';
            } else if (firstTextItem.strikeout === true || firstTextItem.strikeout === 'true') {
              textStyleAttrs.textDecoration = 'line-through';
            }
            
            // Extract text color
            if (firstTextItem.color && typeof firstTextItem.color === 'string') {
              textStyleAttrs.textColor = firstTextItem.color;
            }
            
            // Break after first field with content (prioritize primary fields)
            break;
          }
        }
      }
    }
    
    // Fallback: check style.textColor if not found in content
    if (!textStyleAttrs.textColor && shape.style?.textColor) {
      textStyleAttrs.textColor = shape.style.textColor;
    }
    
    // Track unmapped defIds
    if (!mapped && shape.defId) {
      stats.unmappedDefIds.add(shape.defId);
      console.warn(`[Creately Converter] ⚠️  Unmapped defId: "${shape.defId}" - defaulting to 'shape' type`);
    }
    
    // Track node types
    const typeCount = stats.nodeTypes.get(nodeType) || 0;
    stats.nodeTypes.set(nodeType, typeCount + 1);
    
    let sex: 'male' | 'female' | 'unknown' | undefined = undefined;
    let sexAttrs: Record<string, unknown> = {};
    
    if (nodeType === 'person') {
      const sexResult = getSexAndAttrsFromDefId(shape.defId || '');
      sex = sexResult.sex;
      if (sexResult.attrs) {
        sexAttrs = { ...sexAttrs, ...sexResult.attrs };
      }
      
      // Fallback: check text content if defId doesn't clearly indicate sex
      if (sex === 'unknown' && text) {
        const lowerText = text.toLowerCase();
        if (lowerText.includes('female') || lowerText.includes('woman')) {
          sex = 'female';
        } else if (lowerText.includes('male') || lowerText.includes('man')) {
          sex = 'male';
        }
      }
    }
    const style = extractStyle(shape);
    
    // Extract additional metadata from data
    const dataAttrs: Record<string, unknown> = {};
    if (shape.data) {
      // Extract common fields
      if (shape.data.description?.value) {
        dataAttrs.description = shape.data.description.value;
      }
      if (shape.data.iconType?.value) {
        dataAttrs.iconType = shape.data.iconType.value;
      }
    }

    // Preserve legacy text color for shape-like nodes so internal label colors
    // survive conversion (flowchart/basic/orgchart cards).
    if (typeof textStyleAttrs.textColor === 'string' && textStyleAttrs.textColor.trim().length > 0) {
      dataAttrs.textColor = textStyleAttrs.textColor;
    }

    if (orgNodeMapping?.attrs) {
      Object.assign(dataAttrs, orgNodeMapping.attrs);
    }

    if (legacyOrgPersonText?.name) {
      dataAttrs.name = legacyOrgPersonText.name;
    }
    if (legacyOrgPersonText?.role) {
      dataAttrs.role = legacyOrgPersonText.role;
      dataAttrs.title = legacyOrgPersonText.role;
    }

    const legacyOrgPhoto = isLegacyOrgPersonPhotoNodeType(nodeType)
      ? extractLegacyOrgPhotoRef(shape, options)
      : null;
    if (legacyOrgPhoto?.rawRef) {
      dataAttrs.legacyPhotoRef = legacyOrgPhoto.rawRef;
    }
    if (legacyOrgPhoto?.photoRef) {
      dataAttrs.photoRef = legacyOrgPhoto.photoRef;
    }
    
    // Process freehand shapes: parse instructions to points
    // The new app expects points to be normalized (relative to shape position)
    // and the shape position should be set to the bounding box origin
    if (nodeType === 'freehand' && shape.instructions) {
      const scaleX = shape.scaleX ?? 1;
      const scaleY = shape.scaleY ?? 1;
      const absolutePoints = parseFreehandInstructions(shape.instructions, x, y, scaleX, scaleY);
      
      if (absolutePoints.length >= 4) {
        // Find bounding box of absolute points
        let minX = Infinity;
        let minY = Infinity;
        for (let i = 0; i < absolutePoints.length; i += 2) {
          const px = absolutePoints[i] as number;
          const py = absolutePoints[i + 1] as number;
          if (Number.isFinite(px) && Number.isFinite(py)) {
            if (px < minX) minX = px;
            if (py < minY) minY = py;
          }
        }
        
        if (Number.isFinite(minX) && Number.isFinite(minY)) {
          // Normalize points to be relative to the bounding box origin
          const normalizedPoints: number[] = [];
          for (let i = 0; i < absolutePoints.length; i += 2) {
            const px = absolutePoints[i] as number;
            const py = absolutePoints[i + 1] as number;
            if (Number.isFinite(px) && Number.isFinite(py)) {
              normalizedPoints.push(px - minX, py - minY);
            }
          }
          
          // Store normalized points in attrs (new app expects points in attrs.points)
          dataAttrs.points = normalizedPoints;
          
          // Update shape position to bounding box origin
          // This will override the x, y values set earlier
          x = minX;
          y = minY;
          
          // Extract stroke properties from style
          if (style.strokeColor) {
            dataAttrs.strokeColor = style.strokeColor;
          } else if (shape.style?.lineColor) {
            dataAttrs.strokeColor = shape.style.lineColor;
          }
          if (style.strokeWidth) {
            dataAttrs.strokeWidth = style.strokeWidth;
          } else if (shape.style?.lineThickness) {
            dataAttrs.strokeWidth = shape.style.lineThickness;
          }
          
          // Set default line cap and join for freehand
          dataAttrs.lineCap = 'round';
          dataAttrs.lineJoin = 'round';
        }
      }
    }
    
    // Extract variant from defId for basic-shape and flowchart-shape nodes
    let shapeVariant: string | undefined = undefined;
    if (nodeType === 'basic-shape' || nodeType === 'flowchart-shape' || nodeType === 'org-shape') {
      const defId = shape.defId || '';
      const lowerDefId = defId.toLowerCase();
      
      // Map concept map shapes to appropriate variants
      if (lowerDefId.includes('concept-map.bubble') || lowerDefId.includes('conceptmap.bubble')) {
        shapeVariant = 'circle';
      } else if (lowerDefId.includes('concept-map.cell') || lowerDefId.includes('conceptmap.cell')) {
        shapeVariant = 'rectangle';
      }
      
      // Extract variant from basic shapes
      // Note: Only these variants are supported: rectangle, circle, triangle, diamond, hexagon, pentagon, star
      if (nodeType === 'basic-shape') {
        if (lowerDefId.includes('rectangle') || lowerDefId.includes('square') || lowerDefId.includes('rounded')) {
          // Map rounded rectangle to regular rectangle (closest match)
          shapeVariant = 'rectangle';
        } else if (lowerDefId.includes('circle') || lowerDefId.includes('ellipse')) {
          shapeVariant = 'circle';
        } else if (lowerDefId.includes('triangle')) {
          // Map right triangle to regular triangle (closest match)
          shapeVariant = 'triangle';
        } else if (lowerDefId.includes('diamond')) {
          shapeVariant = 'diamond';
        } else if (lowerDefId.includes('hexagon')) {
          shapeVariant = 'hexagon';
        } else if (lowerDefId.includes('pentagon')) {
          shapeVariant = 'pentagon';
        } else if (lowerDefId.includes('star')) {
          shapeVariant = 'star';
        } else {
          // Fallback: if no specific variant found, default to rectangle
          // This ensures all basic shapes have a variant for proper rendering
          shapeVariant = 'rectangle';
        }
      }
      
      // Extract variant from flowchart shapes via explicit old-defId mapping.
      if (nodeType === 'flowchart-shape') {
        shapeVariant = mapFlowchartVariantFromDefId(defId);
      }
      
      // Add variant to attrs if found
      if (shapeVariant) {
        dataAttrs.variant = shapeVariant;
        dataAttrs.shapeType = shapeVariant; // Also set shapeType for compatibility
      }
    }

    const swimlaneOrientation = getLegacyFlowchartSwimlaneOrientation(shape.defId || '');
    if (nodeType === 'table' && swimlaneOrientation) {
      Object.assign(dataAttrs, buildLegacySwimlaneTableAttrs(shape, swimlaneOrientation, dimensions));
    } else if (nodeType === 'table' && isLegacyBasicTableDefId(shape.defId)) {
      Object.assign(dataAttrs, buildLegacyBasicTableAttrs(shape, dimensions));
    }
    
    // Extract frame type from defId for frame nodes
    if (nodeType === 'frame') {
      const defId = shape.defId || '';
      const lowerDefId = defId.toLowerCase();
      
      let frameType = 'basic'; // default
      if (lowerDefId.includes('grid') || lowerDefId.includes('twobytwo')) {
        frameType = 'grid';
      } else if (lowerDefId.includes('timeline')) {
        frameType = 'board';
      } else if (lowerDefId.includes('swimlane')) {
        frameType = 'layers';
      } else if (lowerDefId.includes('section')) {
        frameType = 'section';
      }
      
      dataAttrs.frameType = frameType;
      // Use the extracted text as title for section frames
      if (text && frameType === 'section') {
        dataAttrs.title = text;
      }
    }

    if (
      text &&
      (nodeType === 'flowchart-shape' || nodeType === 'basic-shape' || nodeType === 'org-shape' || nodeType === 'bpmn-shape') &&
      !dataAttrs.label
    ) {
      dataAttrs.label = text;
    }
    
    // Extract rotation/angle from shape
    // Old app stores rotation as 'angle' (in degrees)
    // In clipboard format: shape.data.angle (top level of data object)
    // In diagram format: shape.angle (top level of shape object)
    // Also check shape.data?.angle as fallback
    let rotation: number | undefined;
    const angleValue = shape.angle ?? shape.data?.angle;
    if (angleValue !== undefined && angleValue !== null) {
      rotation = typeof angleValue === 'number' ? angleValue : parseFloat(String(angleValue));
      if (!isNaN(rotation)) {
        // Store in attrs.rotation (as expected by useDiagramRealtimeWithProjection)
        dataAttrs.rotation = rotation;
      }
    }
    
    // Merge all attrs: sexAttrs (from defId mapping) + dataAttrs (from shape data) + textStyleAttrs (for person labels)
    // For person nodes, include text style attributes (fontSize, fontFamily, fontWeight, etc.) in attrs
    const attrs = { 
      ...sexAttrs, 
      ...dataAttrs,
      ...(nodeType === 'person' ? textStyleAttrs : {})
    };
    
    
    // Check for deceased status in features (diagram format) or data.features (clipboard format)
    let deceased = false;
    if (shape.features) {
      const statusFeature = shape.features.find((f) => f.id === 'status');
      if (statusFeature?.data?.value) {
        const statusValue = Array.isArray(statusFeature.data.value)
          ? statusFeature.data.value[0]?.value
          : statusFeature.data.value;
        deceased = statusValue === 'deceased';
      }
    } else if (shape.data?.features) {
      const statusFeature = shape.data.features.find((f: any) => f.id === 'status');
      if (statusFeature?.data?.value) {
        const statusValue = Array.isArray(statusFeature.data.value)
          ? statusFeature.data.value[0]?.value
          : statusFeature.data.value;
        deceased = statusValue === 'deceased';
      }
    }

    const resolvedNodeName = legacyOrgPersonText?.name || text;
    
    
    const node: Node = {
      id: shape.id,
      type: nodeType,
      x,
      y,
      width: dimensions.width,
      height: dimensions.height,
      zIndex: shape.zIndex,
      // Set name for person, shape, and frame nodes; text for text/sticky-note nodes
      ...(resolvedNodeName && (nodeType === 'text' || nodeType === 'sticky-note'
        ? { text }
        : (nodeType === 'flowchart-shape' || nodeType === 'basic-shape' || nodeType === 'org-shape' || nodeType === 'bpmn-shape')
          ? { text, name: text }
          : { name: resolvedNodeName })),
      // Set fontSize for text and sticky-note nodes
      ...(fontSize && (nodeType === 'text' || nodeType === 'sticky-note') && { fontSize }),
      ...(nodeType === 'person' && sex && { sex }),
      ...(deceased && { deceased }),
      ...(style.fillColor && { fillColor: style.fillColor }),
      ...(style.strokeColor && { strokeColor: style.strokeColor }),
      ...(style.strokeWidth && { strokeWidth: style.strokeWidth }),
      // attrs should contain sexualOrientation and rotation - NOT at top level
      // Make sure we're not accidentally spreading attrs properties to top level
      ...(Object.keys(attrs).length > 0 && { attrs }),
    };
    
    nodes.push(node);
    stats.nodesCreated++;
  }

  const orgLikeNodeCount = nodes.filter((node) => isOrgchartLikeNodeType(node.type)).length;
  const isOrgchartHeavyDiagram = nodes.length > 0 && (orgLikeNodeCount / nodes.length) >= 0.6;
  
  // Second pass: process connectors and create edges
  // Process all shapes (including connectors) to create edges
  for (const shape of shapes) {
    // Process connectors (type === 'connector' means it's an edge)
    if (shape.type === 'connector' && shape.path) {
      // Connector shapes without a valid id would produce edges with id===undefined,
      // which corrupts the downstream Y.Doc (parentSub becomes undefined at encode time).
      if (typeof shape.id !== 'string' || shape.id.length === 0) {
        console.warn('[Creately Converter] Skipping connector with missing id; defId:', shape.defId);
        continue;
      }
      const path = shape.path;
      const head = path[path.headId as string];
      const tail = path[path.tailId as string];
      
      if (head && tail && typeof head === 'object' && typeof tail === 'object') {
        const fromShapeId = head.shapeId;
        const toShapeId = tail.shapeId;
        
        const lowerConnectorDefId = String(shape.defId || '').toLowerCase();
        const isPlainConnectorDefault = lowerConnectorDefId.includes('creately.connector.default');

        // Legacy `creately.connector.default` can represent a "free line" (no attached shapes) or
        // a partially attached line. Map these to the new app's basic line edge model using
        // `__freeF/__freeT` endpoints and explicit fromPoint/toPoint geometry.
        if (isPlainConnectorDefault && (!fromShapeId || !toShapeId)) {
          const edgeId = shape.id;
          const freeFromId = `__freeF:${edgeId}`;
          const freeToId = `__freeT:${edgeId}`;

          const fromId = fromShapeId || freeFromId;
          const toId = toShapeId || freeToId;

          const edgeAttrs: Record<string, unknown> = {};
          const routingType = mapEntryClassToRoutingType(shape.entryClass);
          edgeAttrs.routingType = routingType;

          // Only set explicit geometry for the free endpoint(s).
          const headX = typeof (head as any).x === 'number' ? (head as any).x : undefined;
          const headY = typeof (head as any).y === 'number' ? (head as any).y : undefined;
          const tailX = typeof (tail as any).x === 'number' ? (tail as any).x : undefined;
          const tailY = typeof (tail as any).y === 'number' ? (tail as any).y : undefined;
          if (!fromShapeId && typeof headX === 'number' && typeof headY === 'number') {
            (edgeAttrs as any).fromPoint = { x: headX, y: headY };
          }
          if (!toShapeId && typeof tailX === 'number' && typeof tailY === 'number') {
            (edgeAttrs as any).toPoint = { x: tailX, y: tailY };
          }

          const legacyLabel = extractLegacyConnectorLabel(shape);
          if (legacyLabel.label) {
            (edgeAttrs as any).label = legacyLabel.label;
          }
          if (typeof legacyLabel.labelOffset === 'number') {
            (edgeAttrs as any).labelOffset = legacyLabel.labelOffset;
          }

          const edge: Edge = {
            id: edgeId,
            type: 'line',
            from: fromId,
            to: toId,
            attrs: edgeAttrs,
          };

          edges.push(edge);
          stats.edgesCreated++;
          const edgeTypeCount = stats.edgeTypes.get('line') || 0;
          stats.edgeTypes.set('line', edgeTypeCount + 1);
          continue;
        }

        if (!fromShapeId || !toShapeId) {
          continue;
        }
        
        // Handle connectors that start from connector bars (marriage bars) - these are child relationships
        if (connectorBarMap.has(fromShapeId)) {
          const bar = connectorBarMap.get(fromShapeId);
          if (bar && toShapeId) {
            // Connector from marriage bar to child - create child edge
            // Connect to the first parent (shapeAId) as the parent
            const toNode = nodes.find(n => n.id === toShapeId);
            if (toNode) {
              const edge: Edge = {
                id: shape.id,
                type: 'child',
                from: bar.shapeAId, // Connect to first parent
                to: toShapeId,
                attrs: {},
              };
              edges.push(edge);
              stats.edgesCreated++;
              const edgeTypeCount = stats.edgeTypes.get('child') || 0;
              stats.edgeTypes.set('child', edgeTypeCount + 1);
            } else {
              console.warn(`[Creately Converter] ⚠️  Child connector ${shape.id} points to non-existent node: ${toShapeId}`);
            }
          }
          continue; // Skip further processing for this connector
        }
        
        // Handle connectors that end at connector bars
        if (connectorBarMap.has(toShapeId)) {
          const bar = connectorBarMap.get(toShapeId);
          if (bar && fromShapeId) {
            // Connector from person to marriage bar - create child edge
            const fromNode = nodes.find(n => n.id === fromShapeId);
            if (fromNode) {
              const edge: Edge = {
                id: shape.id,
                type: 'child',
                from: bar.shapeAId, // Connect to first parent
                to: fromShapeId,
                attrs: {},
              };
              edges.push(edge);
              stats.edgesCreated++;
              const edgeTypeCount = stats.edgeTypes.get('child') || 0;
              stats.edgeTypes.set('child', edgeTypeCount + 1);
            } else {
              console.warn(`[Creately Converter] ⚠️  Child connector ${shape.id} points from non-existent node: ${fromShapeId}`);
            }
          }
          continue; // Skip further processing for this connector
        }
        
        // Handle connectors between two nodes
        const fromNode = nodes.find(n => n.id === fromShapeId);
        const toNode = nodes.find(n => n.id === toShapeId);
        
        if (!fromNode || !toNode) {
          continue; // Skip if either end is not a real node
        }
        
        // Determine edge type and attributes from connector defId
        // This handles genogram-specific connector types (divorced, friendship, etc.)
        const { type: edgeTypeFromDefId, attrs: defIdAttrs } = getEdgeTypeAndAttrsFromDefId(shape.defId || '');
        
        // Extract style information from connector (lineColor, etc.)
        const styleAttrs: Record<string, unknown> = {};
        if (shape.style?.lineColor) {
          styleAttrs.color = shape.style.lineColor;
        }
        if (shape.style?.lineThickness) {
          styleAttrs.width = shape.style.lineThickness;
        }
        
        // Map entryClass to routingType (line style)
        const lowerDefId = (shape.defId || '').toLowerCase();
        const isGenogramConnector = lowerDefId.includes('creately.genogram.');
        const isPlainConnector = !isGenogramConnector && (shape.defId || '').includes('creately.connector.default');
        const isFlowchartConnector = lowerDefId.startsWith('creately.flowchart.');
        const isOrgchartConnector = lowerDefId.startsWith('creately.orgchart.') || lowerDefId.startsWith('creately.org.chart.');
        const fromIsOrgNode = isOrgchartLikeNodeType(fromNode.type);
        const toIsOrgNode = isOrgchartLikeNodeType(toNode.type);
        const shouldPromotePlainOrgLineToReporting =
          isOrgchartHeavyDiagram &&
          isPlainConnector &&
          !isFlowchartConnector &&
          !isOrgchartConnector &&
          edgeTypeFromDefId === 'line' &&
          fromIsOrgNode &&
          toIsOrgNode;
        const entryClassRouting = !isGenogramConnector
          ? mapEntryClassToRoutingType(shape.entryClass)
          : undefined;
        
        if (entryClassRouting) {
          styleAttrs.routingType = entryClassRouting;
        }
        
        // Determine edge type based on context
        let edgeType: Edge['type'];
        let edgeAttrs: Record<string, unknown> = { ...defIdAttrs, ...styleAttrs };

        // Connector labels (legacy stores them in shape.texts[*].content with placement in texts[*].pos)
        // Do not override explicit defId labels (e.g. flowchart decision Yes/No).
        if (typeof (edgeAttrs as any).label !== 'string') {
          const legacyLabel = extractLegacyConnectorLabel(shape);
          if (legacyLabel.label) {
            (edgeAttrs as any).label = legacyLabel.label;
          }
          if (typeof legacyLabel.labelOffset === 'number' && typeof (edgeAttrs as any).labelOffset !== 'number') {
            (edgeAttrs as any).labelOffset = legacyLabel.labelOffset;
          }
        }

        // Avoid renderer fallback to bezier by ensuring non-genogram connectors always
        // carry an explicit routing type when legacy data does not specify one.
        if (!isGenogramConnector && typeof edgeAttrs.routingType !== 'string') {
          if (isFlowchartConnector) {
            edgeAttrs.routingType = 'orthogonalRounded';
          } else if (shouldPromotePlainOrgLineToReporting || isOrgchartConnector) {
            edgeAttrs.routingType = 'rounded';
          } else {
            edgeAttrs.routingType = 'orthogonal';
          }
        }
        
        // Map gluepoint IDs to anchor positions (for plain connectors)
        // These are the standard gluepoint IDs used in the old app
        const gluepointToAnchor: Record<string, 'top' | 'right' | 'bottom' | 'left'> = {
          '0FUuJlNPewl': 'top',
          'BRZfYLOPiRa': 'bottom',
          'pbnadF9Wirq': 'left',
          'P9d4172UDLb': 'right',
        };
        
        // Extract gluepoint IDs from connector path (for plain connectors only)
        // For genogram connectors, we use the new app's standards (auto-calculated)
        // Note: isGenogramConnector and isPlainConnector are already defined above
        
        const fromPointAnchor = inferAnchorFromPoint(fromNode, {
          x: typeof (head as any).x === 'number' ? (head as any).x : undefined,
          y: typeof (head as any).y === 'number' ? (head as any).y : undefined,
        });
        const toPointAnchor = inferAnchorFromPoint(toNode, {
          x: typeof (tail as any).x === 'number' ? (tail as any).x : undefined,
          y: typeof (tail as any).y === 'number' ? (tail as any).y : undefined,
        });

        if (isPlainConnector && head && tail) {
          // Extract gluepoint IDs from head and tail
          const fromGluepointId = (head as any).gluepointId;
          const toGluepointId = (tail as any).gluepointId;
          
          // Map gluepoint IDs to anchor positions
          if (fromGluepointId && gluepointToAnchor[fromGluepointId]) {
            edgeAttrs.fromAnchor = gluepointToAnchor[fromGluepointId];
          } else if (fromPointAnchor) {
            edgeAttrs.fromAnchor = fromPointAnchor.anchor;
            edgeAttrs.fromOffset = fromPointAnchor.offset;
          }
          if (toGluepointId && gluepointToAnchor[toGluepointId]) {
            edgeAttrs.toAnchor = gluepointToAnchor[toGluepointId];
          } else if (toPointAnchor) {
            edgeAttrs.toAnchor = toPointAnchor.anchor;
            edgeAttrs.toOffset = toPointAnchor.offset;
          }
        } else if (!isGenogramConnector && (isFlowchartConnector || fromPointAnchor || toPointAnchor)) {
          // For flowchart/non-genogram connectors, infer side/offset from endpoint geometry
          // to preserve legacy attachment points more accurately.
          if (fromPointAnchor) {
            edgeAttrs.fromAnchor = fromPointAnchor.anchor;
            edgeAttrs.fromOffset = fromPointAnchor.offset;
          }
          if (toPointAnchor) {
            edgeAttrs.toAnchor = toPointAnchor.anchor;
            edgeAttrs.toOffset = toPointAnchor.offset;
          }
        }
        
        // Check if this connector is actually connected TO the connector bar itself
        // (not just connected to shapes that happen to be connected by a bar)
        // A connector connected to a bar will have fromShapeId or toShapeId equal to the bar's ID
        const connectedToBar = connectorBarMap.has(fromShapeId) || connectorBarMap.has(toShapeId);
        
        const fromIsFlowchartNode = fromNode.type === 'flowchart-shape' || fromNode.type === 'bpmn-shape';
        const toIsFlowchartNode = toNode.type === 'flowchart-shape' || toNode.type === 'bpmn-shape';
        const isFlowchartEdgeByEndpoints = fromIsFlowchartNode && toIsFlowchartNode;

        if (connectedToBar) {
          // This is a child connector from/to a connector bar
          // The actual parent-child relationship was already handled above (lines 1483-1531)
          // So we should skip this connector here, but if we reach here, it means the bar
          // connection wasn't handled above, so we'll treat it as a child edge
          // However, if defId explicitly says it's a marriage, respect that
          if (edgeTypeFromDefId === 'marriage') {
            edgeType = 'marriage';
          } else {
            edgeType = 'child';
          }
        } else if (shouldPromotePlainOrgLineToReporting) {
          edgeType = 'reporting';
          edgeAttrs = {
            relation: 'reports_to',
            dashed: false,
            startArrow: { type: 'none', size: 6 },
            endArrow: { type: 'triangle', size: 8 },
            ...edgeAttrs,
            routingType: 'rounded',
          };
        } else if (!isGenogramConnector && !isOrgchartConnector && isFlowchartEdgeByEndpoints) {
          // In flowchart diagrams, keep connector semantics by mapping between flowchart nodes to sequence flow.
          edgeType = 'sequence_flow' as Edge['type'];
        } else if (isOrgchartConnector && edgeTypeFromDefId !== 'line') {
          // Keep canonical orgchart relationship edge type when defId is recognized.
          edgeType = edgeTypeFromDefId;
        } else if ((shape.defId || '').toLowerCase().startsWith('creately.flowchart.')) {
          // Fallback for explicit flowchart defIds when endpoint types are not flowchart-tagged.
          edgeType = edgeTypeFromDefId;
        } else if (fromNode.type === 'person' && toNode.type === 'person') {
          // Person-to-person connections: use defId type if it's a recognized genogram connector
          
          if (isGenogramConnector) {
            // For genogram connectors, always use the type from defId (could be 'marriage', 'sibling', or 'line')
            edgeType = edgeTypeFromDefId;
          } else {
            // For non-genogram connectors between people, default to marriage
            edgeType = 'marriage';
          }
        } else {
          // Generic connector for non-person nodes (basic shapes, frames, etc.)
          edgeType = 'line';
        }

        // For legacy `creately.connector.default`, always import as a basic line edge
        // (even when it connects two nodes). This prevents implicit promotion to
        // flowchart/org semantics or marriage defaults.
        if (isPlainConnector) {
          edgeType = 'line';
          // Drop semantic-only fields that may have been added by other mappings.
          delete (edgeAttrs as any).relation;
          delete (edgeAttrs as any).startArrow;
          delete (edgeAttrs as any).endArrow;
        }
        
        // Legacy orgchart connectors are authored opposite to runtime reporting semantics.
        // Normalize to child -> manager so org layout/hierarchy is correct after import.
        let edgeFromId = fromShapeId;
        let edgeToId = toShapeId;
        if (edgeType === 'reporting') {
          edgeFromId = toShapeId;
          edgeToId = fromShapeId;

          const prevFromAnchor = edgeAttrs.fromAnchor;
          const prevToAnchor = edgeAttrs.toAnchor;
          const prevFromOffset = edgeAttrs.fromOffset;
          const prevToOffset = edgeAttrs.toOffset;

          if (prevToAnchor !== undefined) {
            edgeAttrs.fromAnchor = prevToAnchor;
          } else {
            delete edgeAttrs.fromAnchor;
          }

          if (prevFromAnchor !== undefined) {
            edgeAttrs.toAnchor = prevFromAnchor;
          } else {
            delete edgeAttrs.toAnchor;
          }

          if (prevToOffset !== undefined) {
            edgeAttrs.fromOffset = prevToOffset;
          } else {
            delete edgeAttrs.fromOffset;
          }

          if (prevFromOffset !== undefined) {
            edgeAttrs.toOffset = prevFromOffset;
          } else {
            delete edgeAttrs.toOffset;
          }
        }

        const edge: Edge = {
          id: shape.id,
          type: edgeType,
          from: edgeFromId,
          to: edgeToId,
          attrs: edgeAttrs,
        };
        
        if (edgeAttrs.fromAnchor) {
          edge.fromAnchor = edgeAttrs.fromAnchor as 'top' | 'right' | 'bottom' | 'left';
          delete edgeAttrs.fromAnchor;
        }
        if (edgeAttrs.toAnchor) {
          edge.toAnchor = edgeAttrs.toAnchor as 'top' | 'right' | 'bottom' | 'left';
          delete edgeAttrs.toAnchor;
        }
        if (typeof edgeAttrs.fromOffset === 'number') {
          edge.fromOffset = edgeAttrs.fromOffset;
          delete edgeAttrs.fromOffset;
        }
        if (typeof edgeAttrs.toOffset === 'number') {
          edge.toOffset = edgeAttrs.toOffset;
          delete edgeAttrs.toOffset;
        }
        
        edges.push(edge);
        stats.edgesCreated++;
        const edgeTypeCount = stats.edgeTypes.get(edgeType) || 0;
        stats.edgeTypes.set(edgeType, edgeTypeCount + 1);
      }
    }
    
    // Process connector bars as marriage edges
    if (shape.defId === 'creately.basic.connectorbar') {
      const barData = connectorBarMap.get(shape.id);
      if (barData) {
        // Check if marriage edge already exists
        const existingMarriage = edges.find(
          e => e.type === 'marriage' &&
          ((e.from === barData.shapeAId && e.to === barData.shapeBId) ||
           (e.from === barData.shapeBId && e.to === barData.shapeAId))
        );
        
        if (!existingMarriage) {
          const edge: Edge = {
            id: `marriage-${shape.id}`,
            type: 'marriage',
            from: barData.shapeAId,
            to: barData.shapeBId,
            attrs: {},
          };
          edges.push(edge);
          stats.edgesCreated++;
          const edgeTypeCount = stats.edgeTypes.get('marriage') || 0;
          stats.edgeTypes.set('marriage', edgeTypeCount + 1);
        }
      }
    }
  }
  
  // Validate and filter invalid relationships
  // Build maps for efficient lookup
  const marriages = new Map<string, Set<string>>(); // personId -> Set<spouseIds>
  const parentChild = new Map<string, Set<string>>(); // parentId -> Set<childIds>
  const childParent = new Map<string, Set<string>>(); // childId -> Set<parentIds>
  
  // Build relationship maps
  for (const edge of edges) {
    if (edge.type === 'marriage') {
      // Marriage is bidirectional
      if (!marriages.has(edge.from)) marriages.set(edge.from, new Set());
      if (!marriages.has(edge.to)) marriages.set(edge.to, new Set());
      marriages.get(edge.from)!.add(edge.to);
      marriages.get(edge.to)!.add(edge.from);
    } else if (edge.type === 'child') {
      // Parent-child relationship
      if (!parentChild.has(edge.from)) parentChild.set(edge.from, new Set());
      if (!childParent.has(edge.to)) childParent.set(edge.to, new Set());
      parentChild.get(edge.from)!.add(edge.to);
      childParent.get(edge.to)!.add(edge.from);
    }
  }
  
  // Filter invalid edges
  const validEdges: Edge[] = [];
  const invalidEdges: Edge[] = [];
  
  for (const edge of edges) {
    let isValid = true;
    const reason: string[] = [];
    
    // Check 1: Self-referential child edge (person cannot be their own parent)
    if (edge.type === 'child' && edge.from === edge.to) {
      isValid = false;
      reason.push('self-referential (person cannot be their own parent)');
    }
    
    // Check 2: Person cannot be both a spouse and a child of the same person
    if (edge.type === 'child') {
      const childId = edge.to;
      const parentId = edge.from;
      
      // Check if child is married to parent
      const childSpouses = marriages.get(childId);
      if (childSpouses && childSpouses.has(parentId)) {
        isValid = false;
        reason.push(`person ${childId} is both spouse and child of ${parentId}`);
      }
      
      // Check if parent is married to child (reverse check)
      const parentSpouses = marriages.get(parentId);
      if (parentSpouses && parentSpouses.has(childId)) {
        isValid = false;
        reason.push(`person ${parentId} is both spouse and parent of ${childId}`);
      }
    }
    
    // Check 3: Simple cycle detection - prevent direct parent-child cycles
    // (A -> B as child, and B -> A as child creates a cycle)
    if (edge.type === 'child') {
      const childId = edge.to;
      const parentId = edge.from;
      
      // Check if the child is already a parent of the parent (reverse relationship)
      const childsChildren = parentChild.get(childId);
      if (childsChildren && childsChildren.has(parentId)) {
        isValid = false;
        reason.push(`cycle detected: ${parentId} -> ${childId} and ${childId} -> ${parentId}`);
      }
    }
    
    if (isValid) {
      validEdges.push(edge);
    } else {
      invalidEdges.push(edge);
      const fromNode = nodes.find(n => n.id === edge.from);
      const toNode = nodes.find(n => n.id === edge.to);
      const fromName = (fromNode as any)?.name || edge.from;
      const toName = (toNode as any)?.name || edge.to;
      console.warn(
        `[Creately Converter] ⚠️  Dropping invalid ${edge.type} edge "${edge.id}": ${fromName} -> ${toName}. ` +
        `Reason: ${reason.join('; ')}`
      );
    }
  }
  
  // Update edges array and stats
  const droppedCount = invalidEdges.length;
  if (droppedCount > 0) {
    console.warn(`[Creately Converter] ⚠️  Dropped ${droppedCount} invalid relationship(s) during import`);
    stats.edgesCreated -= droppedCount;
    // Update edge type counts
    for (const edge of invalidEdges) {
      const count = stats.edgeTypes.get(edge.type) || 0;
      if (count > 0) {
        stats.edgeTypes.set(edge.type, count - 1);
      }
    }
  }
  
  // Log conversion summary
  console.log(`\n[Creately Converter] ===== Conversion Summary =====`);
  console.log(`Total shapes in diagram: ${stats.totalShapes}`);
  console.log(`  ✓ Nodes created: ${stats.nodesCreated}`);
  console.log(`  ✓ Edges created: ${stats.edgesCreated}`);
  if (droppedCount > 0) {
    console.log(`  ⚠️  Invalid edges dropped: ${droppedCount}`);
  }
  console.log(`  ⊘ Connector bars found: ${stats.connectorBarsFound}`);
  console.log(`  ⊘ Connectors found: ${stats.connectorsFound}`);
  console.log(`  ⊘ Skipped shapes: ${stats.skippedShapes} (connectors + non-genogram shapes)`);
  console.log(`\nNode types created:`);
  for (const [type, count] of Array.from(stats.nodeTypes.entries()).sort()) {
    console.log(`  - ${type}: ${count}`);
  }
  console.log(`\nEdge types created:`);
  for (const [type, count] of Array.from(stats.edgeTypes.entries()).sort()) {
    console.log(`  - ${type}: ${count}`);
  }
  if (stats.unmappedDefIds.size > 0) {
    console.log(`\n⚠️  Unmapped defIds (${stats.unmappedDefIds.size}):`);
    for (const defId of Array.from(stats.unmappedDefIds).sort()) {
      console.log(`  - "${defId}"`);
    }
  }
  console.log(`[Creately Converter] ===============================\n`);
  
  // Convert timestamps
  const createdAt = metadata?.createdAt 
    ? new Date(metadata.createdAt).toISOString() 
    : new Date().toISOString();
  const updatedAt = metadata?.updatedAt 
    ? new Date(metadata.updatedAt).toISOString() 
    : new Date().toISOString();
  
  return {
    version: 1,
    nodes,
    edges: validEdges, // Use validated edges instead of original edges array
    meta: {
      createdAt,
      updatedAt,
    },
  };
}

/**
 * Convert Creately diagram format to new Scene format
 */
export function convertCreatelyDiagramToScene(
  diagramData: CreatelyDiagramExport,
  options?: CreatelyLegacyConverterOptions,
): Scene {
  if (!diagramData.model?.shapes || typeof diagramData.model.shapes !== 'object') {
    return {
      version: 1,
      nodes: [],
      edges: [],
      meta: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  }
  
  const shapes = diagramData.model.shapes;
  const shapeArray = Object.values(shapes);
  const normalizedShapes = shapeArray.map(normalizeDiagramShape);
  
  // Extract metadata timestamps
  const lastUpdated = normalizeNumber(diagramData.model.lastUpdated);
  const createdTime = normalizeNumber(diagramData.static?.createdTime);
  
  return processShapesToScene(normalizedShapes, {
    createdAt: createdTime,
    updatedAt: lastUpdated,
  }, options);
}

/**
 * Convert Creately clipboard export to new Scene format
 */
export function convertCreatelyToScene(
  createlyData: CreatelyClipboardExport,
  options?: CreatelyLegacyConverterOptions,
): Scene {
  if (!createlyData.shapesToCopy || !Array.isArray(createlyData.shapesToCopy)) {
    return {
      version: 1,
      nodes: [],
      edges: [],
      meta: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  }
  
  const normalizedShapes = createlyData.shapesToCopy.map(normalizeClipboardShape);
  
  return processShapesToScene(normalizedShapes, undefined, options);
}

/**
 * Convert and validate the conversion result
 * Automatically detects format (clipboard vs diagram) and converts accordingly
 */
export function convertCreatelyExport(
  createlyData: unknown,
  options?: CreatelyLegacyConverterOptions,
): Scene {
  if (!createlyData || typeof createlyData !== 'object') {
    throw new Error('Invalid Creately export data: must be an object');
  }
  
  const data = createlyData as any;
  
  // Detect format: diagram format has model.shapes (object), clipboard has shapesToCopy (array)
  if (data.model?.shapes && typeof data.model.shapes === 'object') {
    // Diagram format
    return convertCreatelyDiagramToScene(data as CreatelyDiagramExport, options);
  } else if (Array.isArray(data.shapesToCopy)) {
    // Clipboard format
    return convertCreatelyToScene(data as CreatelyClipboardExport, options);
  } else if (data.shapes && typeof data.shapes === 'object') {
    // Alternative diagram format: shapes at root level, wrap in model
    return convertCreatelyDiagramToScene({ model: { shapes: data.shapes, lastUpdated: data.lastUpdated }, static: data.static } as CreatelyDiagramExport, options);
  } else {
    // Provide more detailed error message
    const availableKeys = Object.keys(data).slice(0, 10).join(', ');
    throw new Error(`Invalid Creately export format: expected model.shapes (object) or shapesToCopy (array). Available keys: ${availableKeys}`);
  }
}
